/**
 * SE2 · Build your kit — behaviour for sections/se2-kit-builder.liquid (the one-page configurator, rebuilt
 * 2026-09-29; spec .design/specs/quiz-kit-2026-09-29.md, section C2 and the contracts in D).
 *
 * The section renders the cold default (the default diffuser, no fragrance). This script owns everything after:
 * it reads the arrival (?diffuser= / #handle / ?scent= / ?mood= / ?family= / ?space= / ?tab=, the session, the quiz
 * handoff), keeps one state, and renders every price line, label, tab sub-label, stage bottle and bar figure from
 * it in integer cents, from the JSON island. Nothing is priced from a typed percentage.
 *
 * THE FREE FIRST BOTTLE (owner decisions 2026-09-29): free = the first chosen line, in choice order, whose product
 * has a sellable bottle of the offer size on a plan (a "candidate") and which is on Subscribe & save. Bought
 * one-time it is charged. The page calls it free only while the island says the offer is live and this session has
 * not seen the cart reject the code (se2-kit:offer). After the add, the cart itself decides: the code is applied,
 * verified on the returned cart, and taken off again when it does not apply, with a notice panel in the bar. Until
 * the next edit the page then shows what the cart did (giftCharged): no line is called free unless the cart made it
 * free, the bar shows what the cart charges for the kit, and the notice's actions change the kit with the cart.
 * Codes the shopper had on the cart are always put back; only a code that applies can be named as the conflict.
 *
 * Contracts it keeps (spec D): se2:kit:ready, se2:kit:applied, se2:kit:added, se2:quiz:complete/close (listened),
 * shopify:cart:lines-update through CartLinesUpdateEvent, storage keys se2-kit:v2:<id>, se2-kit:touched,
 * se2-kit:offer, and the data-* / data-testid hooks the acceptance tests use.
 */
(() => {
  'use strict';

  const KIT_PARAMS = ['diffuser', 'scent', 'mood', 'family', 'space', 'tab', 'src', 'quiz', 'step'];
  const NEIGHBOURS = {
    emotion: {
      relax: ['destress', 'fresh-clean', 'grounding'],
      comfort: ['grounding', 'sensual'],
      energy: ['fresh-clean'],
      sensual: ['comfort', 'grounding'],
    },
    scent: {
      fresh: ['marine', 'citrus', 'aromatic'],
      wood: ['amber', 'aromatic'],
      floral: ['fruity'],
      gourmand: ['amber', 'fruity'],
    },
  };
  const EASE = 'cubic-bezier(0.2, 0.7, 0.2, 1)';

  /* ---- small helpers ------------------------------------------------------------------------------------------ */
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const narrow = () => window.matchMedia('(max-width: 989px)').matches;
  const jsUrl = (u) => (/\.js$/.test(u) ? u : u + '.js');
  const store = (kind) => ({
    get(key) {
      try {
        return window[kind].getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        window[kind].setItem(key, value);
      } catch (e) {
        /* storage blocked: the page still works, it just forgets */
      }
    },
    remove(key) {
      try {
        window[kind].removeItem(key);
      } catch (e) {
        /* nothing to do */
      }
    },
  });
  const session = store('sessionStorage');
  const local = store('localStorage');
  const readJson = (s, key) => {
    try {
      const raw = s.get(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };
  // The renewal is one phrase, "$39.99 every 30 days", and a size is one word, "120 mL": neither wraps on any line
  // (canonical offer wording, 2026-10-05). [size], [every] and the offer tokens fill with no-break spaces, and
  // "[price] [every]" / "[plan_price] [every]" are joined by one. [kit_offer] and [kit_offer_renew], the offer's
  // tokens on every other surface (snippets/se2-kit-offer.liquid), fill from the same size, price and cadence.
  const NBSP = ' ';
  const NB_KEYS = ['size', 'every', 'kit_offer', 'kit_offer_renew'];
  const fill = (template, map) => {
    const m = Object.assign({}, map);
    const price = m.plan_price != null ? m.plan_price : m.price;
    if (m.kit_offer == null && m.size != null) m.kit_offer = m.size;
    if (m.kit_offer_renew == null && price != null && m.every) m.kit_offer_renew = price + ' ' + m.every;
    return String(template || '')
      .replace(/\[(plan_price|price)\] \[every\]/g, '[$1]' + NBSP + '[every]')
      .replace(/\[(\w+)\]/g, (match, key) => {
        if (!(key in m) || m[key] == null) return match;
        const value = String(m[key]);
        return NB_KEYS.includes(key) ? value.replace(/ /g, NBSP) : value;
      });
  };
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };
  const publish = (name, payload) => {
    try {
      window.Shopify?.analytics?.publish?.(name, payload);
    } catch (e) {
      /* analytics is optional */
    }
  };
  const dedupe = (codes) => {
    const seen = new Set();
    return codes.filter((code) => {
      const key = String(code || '').trim().toUpperCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));
  // Lookups keyed by a URL value read own properties only: ?mood=constructor must not reach Object.prototype
  // (QA 2026-10-06, S-kit-06).
  const own = (obj, key) => Boolean(obj) && Object.prototype.hasOwnProperty.call(obj, key);
  const FEEL_KEY = /^[a-z0-9][a-z0-9-]{0,39}$/;
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  // The purchase option's name never breaks across a line ("Subscribe / & save").
  const nb = (text) => String(text || '').replace(/Subscribe & save/g, 'Subscribe\u00a0&\u00a0save');
  /** Island fragrance records: the ones that sell first, then collection order. */
  const byStock = (a, b) => Number(Boolean(b.available)) - Number(Boolean(a.available)) || (a.order || 0) - (b.order || 0);
  const upper = (code) => String(code || '').trim().toUpperCase();
  /** Any automatic discount on a cart, at cart level or on a line. */
  const hasAutomatic = (cart) =>
    Boolean(cart) &&
    ((cart.cart_level_discount_applications || []).some((a) => a.type === 'automatic') ||
      (cart.items || []).some((i) =>
        (i.line_level_discount_allocations || []).some((x) => x.discount_application && x.discount_application.type === 'automatic')
      ));
  // Line sources: a line put there by the quiz or by an arrival link is replaced by the next such pick; a line the
  // shopper ticked by hand has no source and stays.
  const PICK_SOURCES = ['quiz', 'url'];

  function withDelimiters(cents, precision, thousands, decimal) {
    const negative = cents < 0;
    const abs = Math.abs(Math.round(cents));
    let out;
    if (precision === 0) {
      out = String(Math.round(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    } else {
      out =
        String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, thousands) +
        decimal +
        String(abs % 100).padStart(2, '0');
    }
    return (negative ? '-' : '') + out;
  }

  function money(cents, format) {
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const match = format ? format.match(pattern) : null;
    if (!match) return '$' + withDelimiters(cents, 2, ',', '.');
    let value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }
    return format.replace(pattern, value);
  }

  async function readResponse(response) {
    let body = null;
    try {
      body = await response.json();
    } catch (e) {
      body = null;
    }
    return { ok: Boolean(response.ok && body && typeof body === 'object' && !body.status), status: response.status, body };
  }

  async function postJson(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    return readResponse(response);
  }

  async function getCart(url) {
    const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' } });
    const result = await readResponse(response);
    if (!result.ok) throw new Error('cart ' + response.status);
    return result.body;
  }

  /* ================================================================================================================
     One kit.
     ================================================================================================================ */
  class Kit {
    constructor(root) {
      this.root = root;
      this.id = root.getAttribute('data-section-id') || '';
      this.stateKey = 'se2-kit:v2:' + this.id;
      this.cleanups = [];
      this.busy = false;
      // added: the kit is in the cart as configured (the CTA reads View cart). giftCharged: that add did not end
      // with the bottle free in the cart, so until the next edit no line is called free (the cart is the truth).
      // addedOnce: a kit went in during this visit, so an edited kit is "another kit". kitCart: the cart the last
      // add or notice action returned.
      this.added = false;
      this.giftCharged = false;
      this.addedOnce = false;
      this.kitCart = null;
      this.autoBefore = false;
      this.notice = null;
      this.planDefaultAllowed = true;
      this.listSignature = '';
      // pinned: the fragrance ids chosen on arrival (a ?scent= or ?mood= link, the session restore, the quiz
      // handoff), in choice order. The "All" list shows them first (KV-02); the shopper's own taps never reorder it.
      // linkPinned: the ones a link chose (line source 'url'), which lead "For you" as well.
      this.pinned = [];
      this.linkPinned = [];
      this.stuck = false;
      this.announced = '';
      // chipGroup: the half of the chip row on show, 'emotion' or 'scent' (the Emotion / Scent toggle, critique
      // 2026-10-15). Ephemeral: never in the address or in storage; the chosen chip's own half wins on every render.
      this.chipGroup = null;
      // The free moment (critique 2026-10-15, recommendation 3): one coordinated motion when a shopper's own tap
      // makes a bottle free. freeMoment is the transient epoch one render consumes (the stage bottle rises in and its
      // Free tag glints, "Free today" pops, the total ticks); freePlayed keeps it to once per kit on this page view,
      // so switching away and back never replays it. Arrival, restore and the quiz handoff never set it.
      this.freeMoment = false;
      this.freePlayed = false;
    }

    /* ---- boot ------------------------------------------------------------------------------------------------ */
    init() {
      const island = this.root.querySelector('script[type="application/json"][data-se2-kit-data]');
      try {
        this.data = JSON.parse(island ? island.textContent : '{}');
      } catch (e) {
        this.data = {};
      }
      this.L = this.data.labels || {};
      this.diffusers = this.data.diffusers || [];
      // In stock first (owner decision 2026-10-06): sold-out oils stay listed, disabled, after the ones that sell, in
      // collection order within each part. The section prints the cards in this order too, so nothing moves on boot.
      this.fragrances = (this.data.fragrances || []).slice().sort(byStock);
      this.max = Math.max(1, Number(this.data.maxFragrances) || 4);
      this.offerCode = (this.data.offer && this.data.offer.code) || '';
      this.offerLive = Boolean(this.data.offer && this.data.offer.live);
      const offerMemo = readJson(session, 'se2-kit:offer');
      if (offerMemo && offerMemo.state === 'unavailable') this.offerLive = false;

      this.query();
      session.remove('se2-kit:' + this.id);

      this.quizMemo = this.readQuiz();
      this.storedFeel = this.quizMemo && this.quizMemo.feel ? this.validFeel(this.quizMemo.feel) : null;

      const arrival = this.readArrival();
      this.state = this.buildState(arrival);
      this.normalize(false);
      this.pinChosen();

      this.bind();
      this.measureRenewFit();
      this.renderAll({ initial: true });
      this.root.setAttribute('data-js', 'on');
      this.setupObservers();

      if (arrival.named) {
        session.set('se2-kit:touched', '1');
        this.dispatchApplied('url');
      }
      if (arrival.named || arrival.hasQuizParam) this.cleanAddress();
      this.save();

      if (arrival.scrollToList) {
        requestAnimationFrame(() => this.scrollToTabs(true));
      }

      document.dispatchEvent(new CustomEvent('se2:kit:ready', { bubbles: true, detail: { sectionId: this.id } }));
    }

    query() {
      const q = (sel) => this.root.querySelector(sel);
      this.announcer = q('[data-se2-kit-announce]');
      this.stage = q('[data-se2-kit-stage]');
      this.comp = q('[data-se2-kit-comp]');
      this.dslot = q('[data-se2-kit-dslot]');
      this.bottles = q('[data-se2-kit-bottles]');
      this.cap1 = q('[data-se2-kit-cap1]');
      this.cap2 = q('[data-se2-kit-cap2]');
      this.halos = Array.from(this.root.querySelectorAll('[data-se2-kit-halo]'));
      this.title = q('[data-testid="se2-kit-title"]');
      this.quizLink = q('[data-se2-kit-quizlink]');
      this.tabbar = q('[data-se2-kit-tabbar]');
      this.mini = q('[data-se2-kit-mini]');
      this.miniD = q('[data-se2-kit-minid]');
      this.miniB = q('[data-se2-kit-minib]');
      this.tabInd = q('[data-se2-kit-tabind]');
      this.tabButtons = Array.from(this.root.querySelectorAll('[data-se2-kit-tab-btn]'));
      this.panels = {
        diffusers: q('[data-se2-kit-panel="diffusers"]'),
        fragrances: q('[data-se2-kit-panel="fragrances"]'),
      };
      this.specs = q('[data-se2-kit-specs]');
      this.rows = Array.from(this.root.querySelectorAll('[data-se2-kit-diffuser]'));
      this.groups = Array.from(this.root.querySelectorAll('[data-se2-kit-group]'));
      this.altButtons = Array.from(this.root.querySelectorAll('[data-se2-kit-alt]'));
      this.chips = Array.from(this.root.querySelectorAll('[data-se2-kit-chip]'));
      this.chipRow = q('[data-se2-kit-chips]');
      this.chipSeg = q('[data-se2-kit-chipseg]');
      this.chipSetButtons = Array.from(this.root.querySelectorAll('[data-se2-kit-chipset-btn]'));
      this.chipNav = Array.from(this.root.querySelectorAll('[data-se2-kit-chipnav]'));
      this.flist = q('[data-se2-kit-flist]');
      this.cards = Array.from(this.root.querySelectorAll('[data-se2-kit-fragrance]'));
      this.eyebrows = {
        matches: q('[data-se2-kit-feyebrow="matches"]'),
        more: q('[data-se2-kit-feyebrow="more"]'),
      };
      this.noMatch = q('[data-se2-kit-nomatch]');
      this.bar = q('[data-se2-kit-bar]');
      this.noticeEl = q('[data-se2-kit-notice]');
      this.renew = q('[data-se2-kit-renew]');
      this.renewText = q('[data-se2-kit-renew-text]');
      this.total = q('[data-se2-kit-total]');
      this.totalFig = q('[data-se2-kit-totalfig]');
      this.totalLabel = q('[data-se2-kit-totallabel]');
      this.cta = q('[data-se2-kit-cta]');
      this.sheet = q('[data-se2-kit-sheet]');
      this.sheetBody = q('[data-se2-kit-sheet-body]');
      this.sentinels = {
        header: q('[data-se2-kit-sentinel="header"]'),
        stuck: q('[data-se2-kit-sentinel="stuck"]'),
      };
    }

    /* ---- catalogue lookups -------------------------------------------------------------------------------- */
    diffuserByProduct(id) {
      return this.diffusers.find((d) => String(d.productId) === String(id)) || null;
    }

    diffuserOfVariant(id) {
      for (const d of this.diffusers) {
        const v = (d.variants || []).find((x) => String(x.id) === String(id));
        if (v) return { product: d, variant: v };
      }
      return null;
    }

    fragrance(id) {
      return this.fragrances.find((f) => String(f.id) === String(id)) || null;
    }

    fvariant(product, id) {
      if (!product) return null;
      return (product.variants || []).find((v) => String(v.id) === String(id)) || null;
    }

    firstSellable(d) {
      if (!d || !d.available) return null;
      return (d.variants || []).find((v) => String(v.id) === String(d.defaultVariant) && v.available) ||
        (d.variants || []).find((v) => v.available) ||
        null;
    }

    /** A diffuser named by handle, product id or variant id; sold-out names return null. */
    resolveDiffuser(raw) {
      let want = '';
      try {
        want = decodeURIComponent(String(raw || '')).trim().toLowerCase();
      } catch (e) {
        want = String(raw || '').trim().toLowerCase();
      }
      if (!want) return null;
      if (/^\d+$/.test(want)) {
        const byProduct = this.diffuserByProduct(want);
        if (byProduct) {
          const v = this.firstSellable(byProduct);
          return v ? { product: byProduct, variant: v } : null;
        }
        const byVariant = this.diffuserOfVariant(want);
        if (byVariant && byVariant.variant.available) return byVariant;
        return null;
      }
      const d = this.diffusers.find((x) => String(x.handle).toLowerCase() === want);
      if (!d) return null;
      const v = this.firstSellable(d);
      return v ? { product: d, variant: v } : null;
    }

    resolveScent(raw) {
      let want = '';
      try {
        want = decodeURIComponent(String(raw || '')).trim().toLowerCase();
      } catch (e) {
        want = String(raw || '').trim().toLowerCase();
      }
      if (!want) return null;
      if (/^\d+$/.test(want)) return this.fragrance(want);
      return (
        this.fragrances.find((f) => {
          const h = String(f.handle).toLowerCase();
          return h === want || h === want + '-fragrance-oil' || h.replace(/-fragrance-oil$/, '') === want;
        }) || null
      );
    }

    defaultDiffuserVariant() {
      const byId = this.diffuserOfVariant(this.data.defaultDiffuser);
      if (byId && byId.variant.available) return byId.variant.id;
      for (const d of this.diffusers) {
        const v = this.firstSellable(d);
        if (v) return v.id;
      }
      return null;
    }

    anySellableFragrance() {
      return this.fragrances.some((f) => f.available);
    }

    spaceFor(key, label, candidates) {
      if (!key && !(candidates && candidates.length)) return null;
      const sellable = (candidates || []).filter((h) => {
        const d = this.diffusers.find((x) => x.handle === h);
        return d && d.available;
      });
      return { key: key || '', label: label || '', candidates: sellable };
    }

    spaceFromKey(rawKey) {
      let key = String(rawKey || '').trim().toLowerCase();
      if (key === 'car') key = 'intimate';
      if (!key) return null;
      const island = document.querySelector('script[data-se2-quiz-data]');
      if (island) {
        try {
          const quiz = JSON.parse(island.textContent);
          const hit = (quiz.spaces || []).find((s) => s.key === key);
          if (hit) return this.spaceFor(key, hit.label, hit.candidates || [hit.diffuser]);
        } catch (e) {
          /* fall through to the island's map */
        }
      }
      // Without the quiz on the page: the same spaces from the kit's island (snippets/se2-kit-explainer.liquid,
      // mode "spacesjson"), one source for both (QA 2026-10-06, S-kit-11).
      const fb = own(this.data.spaces, key) ? this.data.spaces[key] : null;
      return fb ? this.spaceFor(key, fb.label, fb.handles || []) : null;
    }

    readQuiz() {
      const fromSession = readJson(session, 'se2-quiz:v1');
      if (fromSession && fromSession.status) return fromSession;
      const fromLocal = readJson(local, 'se2-quiz:v1');
      if (fromLocal && fromLocal.status) {
        if (Number(fromLocal.exp) > Date.now()) return fromLocal;
        local.remove('se2-quiz:v1');
      }
      return null;
    }

    /** A feeling the kit can rank by: a known emotion or scent key, else null. */
    validFeel(feel) {
      if (!feel) return null;
      const group = feel.group === 'scent' ? 'scent' : 'emotion';
      const key = String(feel.key || '').trim().toLowerCase();
      if (!FEEL_KEY.test(key)) return null;
      const chip = this.chips.some((c) => c.getAttribute('data-se2-kit-chip') === (group === 'emotion' ? 'mood:' : 'family:') + key);
      const known =
        chip ||
        own(NEIGHBOURS[group], key) ||
        this.fragrances.some((f) => ((group === 'emotion' ? f.moods : f.families) || []).includes(key));
      return known ? Object.assign({}, feel, { group, key }) : null;
    }

    feelLabel(group, key) {
      const chip = this.chips.find((c) => c.getAttribute('data-se2-kit-chip') === (group === 'emotion' ? 'mood:' : 'family:') + key);
      const text = chip ? chip.querySelector('.se2-chip__label')?.textContent : '';
      return (text || key.charAt(0).toUpperCase() + key.slice(1)).trim();
    }

    /* ---- the arrival (spec D.1, C2.6) --------------------------------------------------------------------- */
    readArrival() {
      const params = new URLSearchParams(window.location.search);
      const hash = window.location.hash.replace(/^#/, '');
      const out = { named: false, scrollToList: false };
      const has = (k) => params.has(k) && params.get(k) !== '';
      out.named = KIT_PARAMS.some((k) => k !== 'quiz' && has(k)) || /^step-\d+$/.test(hash);
      out.hasQuizParam = params.has('quiz');

      out.diffuser = has('diffuser') ? this.resolveDiffuser(params.get('diffuser')) : null;
      out.diffuserNamed = has('diffuser');
      if (!out.diffuser && hash && !/^(set|step-\d+)$/.test(hash)) {
        const byHash = this.resolveDiffuser(hash);
        if (byHash) {
          out.diffuser = byHash;
          out.diffuserNamed = true;
          out.named = true;
          out.hashDiffuser = true;
        }
      }
      out.scent = has('scent') ? this.resolveScent(params.get('scent')) : null;
      out.scentNamed = has('scent');
      // A mood or family is used only when the kit knows it (a chip, or a fragrance carrying the key); anything
      // else is ignored like an unknown handle (spec D.1; QA 2026-10-06, S-kit-06).
      if (has('mood')) out.feel = this.validFeel({ group: 'emotion', key: params.get('mood') });
      else if (has('family')) out.feel = this.validFeel({ group: 'scent', key: params.get('family') });
      if (out.feel) out.feel.label = this.feelLabel(out.feel.group, out.feel.key);
      out.space = has('space') ? this.spaceFromKey(params.get('space')) : null;
      let tab = has('tab') ? params.get('tab').toLowerCase() : '';
      if (tab === 'aromas') tab = 'fragrances';
      out.tab = tab === 'fragrances' || tab === 'diffusers' ? tab : '';
      out.fromQuiz = params.get('src') === 'quiz';
      return out;
    }

    buildState(arrival) {
      const saved = readJson(session, this.stateKey);
      const state = {
        diffuser: null,
        fragrances: [],
        feel: null,
        space: null,
        tab: 'diffusers',
        chip: 'all',
      };
      if (saved && saved.v === 2) {
        const sd = this.diffuserOfVariant(saved.diffuser);
        if (sd && sd.variant.available) state.diffuser = sd.variant.id;
        state.fragrances = Array.isArray(saved.fragrances) ? saved.fragrances.slice(0, this.max) : [];
        state.feel = saved.feel ? this.validFeel(saved.feel) : null;
        state.space = saved.space && Array.isArray(saved.space.candidates) ? saved.space : null;
        if (saved.tab === 'fragrances' || saved.tab === 'diffusers') state.tab = saved.tab;
        if (typeof saved.chip === 'string') state.chip = saved.chip;
      }
      if (arrival.diffuser) state.diffuser = arrival.diffuser.variant.id;
      if (!state.diffuser) state.diffuser = this.defaultDiffuserVariant();

      if (arrival.space) state.space = arrival.space;
      else if (arrival.diffuser && arrival.diffuserNamed) {
        // A named diffuser without a space: that row carries "For your space" (spec C2.7).
        state.space = { key: '', label: '', candidates: [arrival.diffuser.product.handle] };
      }

      if (arrival.scent && arrival.scent.available) {
        this.pickFirst(state, arrival.scent, 'url');
        // A linked fragrance is never hidden behind a filter chip restored from earlier in the visit: the list
        // opens on For you (a stored quiz answer) or All, which both show every fragrance with the link's pick
        // first (KV-02, orderList).
        if (state.chip.indexOf(':') > 0) state.chip = 'all';
      }
      if (arrival.feel) {
        state.feel = arrival.feel;
        state.chip = 'for-you';
        if (!state.fragrances.length) {
          const pick = this.topPick(arrival.feel);
          if (pick) this.pickFirst(state, pick, 'url');
        }
      }
      if (state.chip === 'for-you' && !this.feelForRanking(state)) state.chip = 'all';
      if (state.chip === 'all' && this.feelForRanking(state) && !saved) state.chip = 'for-you';

      let tab = '';
      if (arrival.tab) tab = arrival.tab;
      else if (arrival.fromQuiz || arrival.feel) tab = 'fragrances';
      else if (arrival.diffuserNamed && arrival.diffuser) tab = 'fragrances';
      else if (arrival.scentNamed && arrival.scent) tab = 'diffusers';
      if (tab) state.tab = tab;

      if (
        narrow() &&
        state.tab === 'fragrances' &&
        !arrival.fromQuiz &&
        (arrival.tab === 'fragrances' || (arrival.diffuserNamed && arrival.diffuser)) &&
        state.fragrances.length === 0
      ) {
        arrival.scrollToList = true;
      }
      return state;
    }

    feelForRanking(state = this.state) {
      return (state && state.feel) || this.storedFeel || null;
    }

    /* ---- the offer, per line (spec C2.8) ------------------------------------------------------------------ */
    isCandidate(product) {
      return Boolean(this.offerLive && product && product.giftAvailable && product.giftVariant);
    }

    /** The variant a line resolves to: a candidate is locked to its bottle of the offer size. */
    lineVariant(line) {
      const product = this.fragrance(line.productId);
      if (!product) return null;
      if (this.isCandidate(product)) return this.fvariant(product, product.giftVariant);
      return this.fvariant(product, line.variantId) || this.fvariant(product, product.defaultVariant);
    }

    /** The line the offer makes free, by the rule alone: the first candidate on Subscribe & save, in choice order. */
    rawFreeIndex(lines = this.state.fragrances) {
      for (let i = 0; i < lines.length; i += 1) {
        const product = this.fragrance(lines[i].productId);
        const variant = this.lineVariant(lines[i]);
        if (this.isCandidate(product) && lines[i].plan && variant && variant.planId) return i;
      }
      return -1;
    }

    /**
     * The line the page calls free. After an add whose cart did not make the bottle free (not applicable, a
     * conflict, one per order, an error), none is, until the next edit: the cart is the truth (spec C2.10).
     */
    freeIndex(lines = this.state.fragrances) {
      if (this.offerSpent()) return -1;
      return this.rawFreeIndex(lines);
    }

    offerSpent() {
      return this.added && this.giftCharged;
    }

    /** Would a fragrance joining now start on Subscribe & save? Only the would-be free bottle does (OD2). */
    defaultPlan(product, lines) {
      if (!this.isCandidate(product) || !this.planDefaultAllowed) return false;
      if (lines.some((l) => l.plan)) return false;
      const variant = this.fvariant(product, product.giftVariant);
      if (!variant || !variant.planId) return false;
      return this.rawFreeIndex(lines.filter((l) => String(l.productId) !== String(product.id))) < 0;
    }

    addVariantFor(product) {
      if (this.isCandidate(product)) return product.giftVariant;
      return product.defaultVariant;
    }

    /**
     * Puts a product at index 0 (moving it if chosen) with the default purchase option. With a source ('quiz' or
     * 'url'), the previous quiz or link pick goes first, so a retaken quiz or a new ?scent= replaces it instead of
     * stacking; fragrances the shopper ticked by hand stay as extras (spec C2.9).
     */
    pickFirst(state, product, src) {
      if (!product || !product.available) return;
      const same = state.fragrances.find((l) => String(l.productId) === String(product.id)) || null;
      const rest = state.fragrances.filter(
        (l) => String(l.productId) !== String(product.id) && !(src && PICK_SOURCES.includes(l.src))
      );
      const variantId = this.addVariantFor(product);
      if (!variantId) return;
      const line = { productId: product.id, variantId, plan: this.defaultPlan(product, rest) };
      // A fragrance already ticked by hand keeps its hand status; otherwise the line carries its source.
      const keepHand = same && !PICK_SOURCES.includes(same.src);
      if (src && !keepHand) line.src = src;
      state.fragrances = [line].concat(rest).slice(0, this.max);
    }

    /** Validates every line against the live island; never offers an unsellable variant. */
    normalize(announce) {
      const s = this.state;
      const d = this.diffuserOfVariant(s.diffuser);
      if (!d || !d.variant.available) s.diffuser = this.defaultDiffuserVariant();
      const seen = new Set();
      const kept = [];
      for (const raw of s.fragrances || []) {
        const product = this.fragrance(raw && raw.productId);
        if (!product || !product.available || seen.has(String(product.id))) continue;
        seen.add(String(product.id));
        let variant = this.isCandidate(product)
          ? this.fvariant(product, product.giftVariant)
          : this.fvariant(product, raw.variantId);
        if (!variant || !variant.available) variant = this.fvariant(product, product.defaultVariant);
        if (!variant || !variant.available) continue;
        if (announce && String(variant.id) !== String(raw.variantId)) {
          this.say(fill(this.L.annSizeChanged, { size: variant.title }));
        }
        const line = { productId: product.id, variantId: variant.id, plan: Boolean(raw.plan && variant.planId) };
        if (PICK_SOURCES.includes(raw.src)) line.src = raw.src;
        kept.push(line);
        if (kept.length >= this.max) break;
      }
      // One bottle per kit on Subscribe & save (owner, 2026-10-13): a saved or linked kit with more keeps the first.
      let planSeen = false;
      for (const line of kept) {
        if (!line.plan) continue;
        if (planSeen) line.plan = false;
        planSeen = true;
      }
      s.fragrances = kept;
    }

    totals() {
      const d = this.diffuserOfVariant(this.state.diffuser);
      const lines = this.state.fragrances;
      const free = this.freeIndex(lines);
      let due = d ? d.variant.price : 0;
      let oneTime = due;
      let recurring = 0;
      const cadences = new Set();
      let anyPlan = false;
      lines.forEach((line, i) => {
        const v = this.lineVariant(line);
        if (!v) return;
        if (line.plan && v.planId) {
          anyPlan = true;
          recurring += v.planPrice;
          cadences.add(v.planEvery || '');
          due += i === free ? 0 : v.planPrice;
        } else {
          due += v.price;
          oneTime += v.price;
        }
      });
      const cadence = cadences.size === 1 ? Array.from(cadences)[0] : null;
      return { due, oneTime, recurring, cadence, anyPlan, free, diffuser: d, cartDue: this.kitCartDue() };
    }

    /**
     * While the kit sits in the cart as configured, what the cart charges for its lines (line-level discounts
     * included, e.g. an automatic discount the kit's own lines triggered). Null before an add and after any edit.
     */
    kitCartDue() {
      if (!this.added || !this.kitCart || !this.kitId) return null;
      const lines = (this.kitCart.items || []).filter((i) => i.properties && i.properties._se2_kit === this.kitId);
      if (!lines.length) return null;
      return lines.reduce((sum, i) => sum + (Number(i.final_line_price) || 0), 0);
    }

    fmt(cents) {
      return money(cents, this.data.moneyFormat);
    }

    /* ---- ranking (spec C2.9) ------------------------------------------------------------------------------ */
    rank(feel) {
      if (!feel || !feel.key) return null;
      const group = feel.group === 'scent' ? 'scent' : 'emotion';
      const keysOf = (f) => (group === 'emotion' ? f.moods : f.families) || [];
      const weight = (keys, k) => (keys[0] === k ? 3 : keys[1] === k ? 2 : keys.includes(k) ? 1 : 0);
      const scored = this.fragrances.map((f) => ({ f, score: weight(keysOf(f), feel.key) }));
      if (scored.filter((x) => x.score > 0).length < 2) {
        const neighbours = own(NEIGHBOURS, group) && own(NEIGHBOURS[group], feel.key) ? NEIGHBOURS[group][feel.key] : [];
        scored.forEach((x) => {
          const best = neighbours.reduce((m, n) => Math.max(m, weight(keysOf(x.f), n)), 0);
          x.score += 0.5 * best;
        });
      }
      // in stock first within each group (owner decision 2026-10-06), then the score, then the collection order
      scored.sort((a, b) => Number(Boolean(b.f.available)) - Number(Boolean(a.f.available)) || b.score - a.score || (a.f.order || 0) - (b.f.order || 0));
      let matches = scored.filter((x) => x.score > 0).map((x) => x.f);
      if (matches.length < 2) {
        for (const f of this.fragrances) {
          if (matches.length >= 2) break;
          if (!matches.includes(f)) matches.push(f);
        }
      }
      const ids = new Set(matches.map((f) => String(f.id)));
      const ordered = scored.map((x) => x.f);
      return {
        matches: ordered.filter((f) => ids.has(String(f.id))),
        more: ordered.filter((f) => !ids.has(String(f.id))),
      };
    }

    topPick(feel) {
      const ranked = this.rank(feel);
      if (!ranked) return null;
      for (const f of ranked.matches.concat(ranked.more)) {
        const v = this.fvariant(f, this.addVariantFor(f));
        if (f.available && v && v.available) return f;
      }
      return null;
    }

    /* ---- persistence and the address ---------------------------------------------------------------------- */
    save() {
      const s = this.state;
      session.set(
        this.stateKey,
        JSON.stringify({
          v: 2,
          diffuser: s.diffuser,
          fragrances: s.fragrances.map((l) => {
            const out = { productId: l.productId, variantId: l.variantId, plan: Boolean(l.plan) };
            if (PICK_SOURCES.includes(l.src)) out.src = l.src;
            return out;
          }),
          feel: s.feel,
          space: s.space,
          tab: s.tab,
          chip: s.chip,
        })
      );
    }

    touch() {
      session.set('se2-kit:touched', '1');
    }

    cleanAddress() {
      try {
        const url = new URL(window.location.href);
        KIT_PARAMS.forEach((k) => url.searchParams.delete(k));
        if (/^#step-\d+$/.test(url.hash) || url.hash === '#set') url.hash = '';
        const next = url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') + url.hash;
        if (next !== window.location.pathname + window.location.search + window.location.hash) {
          window.history.replaceState(window.history.state, '', next);
        }
      } catch (e) {
        /* an address we cannot rewrite stays as it is */
      }
    }

    /* ---- events ------------------------------------------------------------------------------------------- */
    on(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.cleanups.push(() => target.removeEventListener(type, handler, options));
    }

    bind() {
      this.on(this.root, 'click', (event) => this.onClick(event));
      this.on(this.root, 'change', (event) => this.onChange(event));
      this.on(this.root, 'keydown', (event) => this.onKeydown(event));
      this.on(document, 'se2:quiz:complete', (event) => this.onQuizComplete(event));
      this.on(document, 'se2:quiz:close', (event) => this.onQuizClose(event));
      if (this.sheet) {
        this.on(this.sheet, 'close', () => this.onSheetClosed());
        this.on(this.sheet, 'click', (event) => {
          if (event.target !== this.sheet) return;
          const r = this.sheet.getBoundingClientRect();
          const inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
          const panel = this.sheet.querySelector('.se2-kit__sheetin');
          const pr = panel ? panel.getBoundingClientRect() : r;
          const inPanel = event.clientX >= pr.left && event.clientX <= pr.right && event.clientY >= pr.top && event.clientY <= pr.bottom;
          if (!inPanel || !inside) this.closeSheet();
        });
      }
      if (this.bar && 'ResizeObserver' in window) {
        let lastWidth = 0;
        const ro = new ResizeObserver(() => {
          this.fitBar();
          const w = this.bar.clientWidth;
          if (w !== lastWidth) {
            lastWidth = w;
            this.measureRenewFit();
            this.placeTabIndicator();
          }
        });
        ro.observe(this.bar);
        this.cleanups.push(() => ro.disconnect());
      }
      this.on(window, 'resize', () => {
        this.placeTabIndicator();
        this.updateChipNav();
      });
      if (this.chipRow && this.chipNav.length) {
        this.on(this.chipRow, 'scroll', () => this.updateChipNav(), { passive: true });
      }
      this.on(window, 'pageshow', (event) => {
        if (event.persisted) this.renderAll({});
      });
    }

    onClick(event) {
      const t = event.target;
      if (!(t instanceof Element)) return;
      const tab = t.closest('[data-se2-kit-tab-btn]');
      if (tab) return this.selectTab(tab.getAttribute('data-se2-kit-tab-btn'), { user: true });
      if (t.closest('[data-se2-kit-showall]')) return this.showAllFragrances();
      const chipSet = t.closest('[data-se2-kit-chipset-btn]');
      if (chipSet) return this.selectChipGroup(chipSet.getAttribute('data-se2-kit-chipset-btn'));
      const chip = t.closest('[data-se2-kit-chip]');
      if (chip) return this.selectChip(chip.getAttribute('data-se2-kit-chip'));
      const chipNav = t.closest('[data-se2-kit-chipnav]');
      if (chipNav) return this.pageChips(Number(chipNav.getAttribute('data-se2-kit-chipnav')) || 1);
      const plan = t.closest('[data-se2-kit-plan]');
      if (plan) return this.setPlan(plan.closest('[data-se2-kit-fragrance]'), plan.getAttribute('data-se2-kit-plan') === 'on');
      const size = t.closest('[data-se2-kit-size]');
      if (size) return this.setSize(size.closest('[data-se2-kit-fragrance]'), size.getAttribute('data-se2-kit-size'));
      const details = t.closest('[data-se2-kit-details]');
      if (details) return this.openSheet('fragrance', details, details.closest('[data-se2-kit-fragrance]').getAttribute('data-product-id'));
      const features = t.closest('[data-se2-kit-features]');
      if (features) return this.openSheet('diffuser', features);
      const alt = t.closest('[data-se2-kit-alt]');
      if (alt) return this.chooseAlternative(alt.getAttribute('data-se2-kit-alt'));
      if (t.closest('[data-se2-kit-total]')) {
        if (this.sheet && this.sheet.open) return this.closeSheet();
        return this.openSheet('breakdown', this.total);
      }
      if (t.closest('[data-se2-kit-cta]')) return this.onCta();
      if (t.closest('[data-se2-kit-mini]')) return this.showStage();
      if (t.closest('[data-se2-kit-sheet-close]')) return this.closeSheet();
      const sheetAct = t.closest('[data-se2-kit-sheet-act]');
      if (sheetAct) return this.onSheetAction(sheetAct);
      const act = t.closest('[data-se2-kit-act]');
      if (act) return this.onNoticeAction(act.getAttribute('data-se2-kit-act'));
      return undefined;
    }

    onChange(event) {
      const t = event.target;
      if (!(t instanceof HTMLInputElement)) return;
      if (t.name === 'se2-kit-diffuser') {
        const row = t.closest('[data-se2-kit-diffuser]');
        if (row) this.chooseDiffuser(row.getAttribute('data-product-id'));
      } else if (t.name === 'se2-kit-color') {
        this.chooseColor(t.value);
      } else if (t.classList.contains('se2-kit__check')) {
        const card = t.closest('[data-se2-kit-fragrance]');
        if (card) this.toggleFragrance(card.getAttribute('data-product-id'), t.checked, card);
      }
    }

    onKeydown(event) {
      const tab = event.target instanceof Element ? event.target.closest('[data-se2-kit-tab-btn]') : null;
      if (!tab) return;
      const order = this.tabButtons;
      let i = order.indexOf(tab);
      if (event.key === 'ArrowRight') i = (i + 1) % order.length;
      else if (event.key === 'ArrowLeft') i = (i - 1 + order.length) % order.length;
      else if (event.key === 'Home') i = 0;
      else if (event.key === 'End') i = order.length - 1;
      else return;
      event.preventDefault();
      const next = order[i];
      this.selectTab(next.getAttribute('data-se2-kit-tab-btn'), { user: true });
      next.focus();
    }

    /* ---- choices ------------------------------------------------------------------------------------------ */
    /**
     * Any edit ends the "as added" state: the offer is judged by its rule again (the next add re-verifies the code
     * in the cart), a stale notice about the last add goes, and the button offers another kit (spec C2.10).
     */
    beginEdit() {
      this.added = false;
      this.giftCharged = false;
      this.kitCart = null;
      if (this.notice) this.hideNotice();
    }

    /** Opens the folded rest of the list and puts focus on the first card that appeared (owner, 2026-10-15). */
    showAllFragrances() {
      const before = new Set(this.cards.filter((c) => !c.hidden).map((c) => c));
      this.showAll = true;
      this.renderAll({});
      const inOrder = this.flist ? Array.from(this.flist.querySelectorAll('[data-se2-kit-fragrance]')) : this.cards;
      const first = inOrder.find((c) => !c.hidden && !before.has(c));
      const target = first && first.querySelector('.se2-kit__check');
      if (target) target.focus({ preventScroll: false });
    }

    changed() {
      this.beginEdit();
      this.touch();
      this.save();
      this.renderAll({});
      // One render consumed the free moment (stage, price card and bar together); it never plays again on this kit.
      if (this.freeMoment) {
        this.freeMoment = false;
        this.freePlayed = true;
      }
    }

    /**
     * Arms the free moment for the render a shopper's own tap is about to cause: only when that tap made a bottle
     * free (a free line appeared, or another line became the free one), only once per kit, never under reduced motion.
     */
    armFreeMoment(freeBeforeId, freeAfterId) {
      if (this.freePlayed || reduced() || !freeAfterId || freeAfterId === freeBeforeId) return false;
      this.freeMoment = true;
      return true;
    }

    chooseDiffuser(productId) {
      const d = this.diffuserByProduct(productId);
      if (!d || !d.available) return;
      const current = this.diffuserOfVariant(this.state.diffuser);
      if (current && String(current.product.productId) === String(d.productId)) return;
      let v = null;
      if (current) v = (d.variants || []).find((x) => x.available && x.color === current.variant.color);
      v = v || this.firstSellable(d);
      if (!v) return;
      this.beginEdit();
      this.state.diffuser = v.id;
      this.say(fill(this.L.annSelected, { title: d.title }));
      this.changed();
    }

    /** A sold-out room band's "Choose …": the closest diffuser in stock, chosen in its own band, focus on its radio. */
    chooseAlternative(productId) {
      this.chooseDiffuser(productId);
      const row = this.rows.find((r) => r.getAttribute('data-product-id') === String(productId));
      const radio = row && row.querySelector('input[name="se2-kit-diffuser"]');
      if (!radio || radio.disabled) return;
      radio.focus({ preventScroll: true });
      this.keepInView(row);
    }

    chooseColor(variantId) {
      const hit = this.diffuserOfVariant(variantId);
      if (!hit || !hit.variant.available) return;
      this.beginEdit();
      this.state.diffuser = hit.variant.id;
      this.say(fill(this.L.annSelected, { title: hit.product.title + ', ' + hit.variant.color }));
      this.changed();
    }

    toggleFragrance(productId, on, card) {
      const product = this.fragrance(productId);
      if (!product) return;
      const lines = this.state.fragrances;
      const index = lines.findIndex((l) => String(l.productId) === String(productId));
      if (on && index < 0) {
        if (!product.available || lines.length >= this.max) {
          this.renderAll({});
          return;
        }
        const variantId = this.addVariantFor(product);
        const variant = this.fvariant(product, variantId);
        if (!variant || !variant.available) {
          this.renderAll({});
          return;
        }
        this.beginEdit();
        const plan = this.defaultPlan(product, lines);
        lines.push({ productId: product.id, variantId: variant.id, plan });
        const isFree = this.freeIndex() === lines.length - 1;
        const message = isFree
          ? fill(this.L.annAddedFree, { title: product.title, size: variant.title, price: this.fmt(variant.planPrice), every: variant.planEvery })
          : plan
            ? fill(this.L.annAddedPlan, { title: product.title, size: variant.title, price: this.fmt(variant.planPrice), every: variant.planEvery })
            : fill(this.L.annAddedOnce, { title: product.title, size: variant.title, price: this.fmt(variant.price) });
        this.say(message);
        const fromRect = this.cardImageRect(card);
        // A bottle that arrives free gets the free moment instead of the fly-to-stage: its own rise is the landing,
        // so the stage never runs two motions for one tap. With the stage off screen the rise plays unseen, so the
        // picture still flies, which can then only reach the condensed mini chip.
        const moment = isFree && this.armFreeMoment('', String(product.id));
        this.changed();
        this.revealPurchase(card);
        if (!moment || !this.inView(this.stage)) this.flyToStage(card, product, fromRect);
      } else if (!on && index >= 0) {
        this.beginEdit();
        const freeBefore = this.freeIndex();
        const freeBeforeId = freeBefore >= 0 ? String(lines[freeBefore].productId) : '';
        lines.splice(index, 1);
        const freeAfter = this.freeIndex();
        const freeAfterId = freeAfter >= 0 ? String(lines[freeAfter].productId) : '';
        if (freeAfterId && freeAfterId !== freeBeforeId) {
          this.say(fill(this.L.annNowFree, { title: this.fragrance(freeAfterId).title }));
        } else {
          this.say(fill(this.L.annRemoved, { title: product.title }));
        }
        this.armFreeMoment(freeBeforeId, freeAfterId);
        this.changed();
      }
    }

    /** The other kit line already on Subscribe & save, if any: a kit holds one (owner, 2026-10-13). */
    planHolder(line) {
      return this.state.fragrances.find((l) => l !== line && l.plan) || null;
    }

    setPlan(card, on) {
      if (!card) return;
      const id = card.getAttribute('data-product-id');
      const line = this.state.fragrances.find((l) => String(l.productId) === String(id));
      if (!line || Boolean(line.plan) === Boolean(on)) return;
      const product = this.fragrance(id);
      const variant = this.lineVariant(line);
      if (!variant || (on && !variant.planId)) return;
      const holder = on ? this.planHolder(line) : null;
      if (holder) {
        const other = this.fragrance(holder.productId);
        this.say(fill(this.L.planTaken, { title: other ? other.title : '' }));
        return;
      }
      this.beginEdit();
      const freeBefore = this.freeIndex();
      const freeBeforeId = freeBefore >= 0 ? String(this.state.fragrances[freeBefore].productId) : '';
      line.plan = Boolean(on);
      const freeAfter = this.freeIndex();
      const freeAfterId = freeAfter >= 0 ? String(this.state.fragrances[freeAfter].productId) : '';
      let message;
      if (freeAfterId && freeAfterId !== freeBeforeId) {
        message = fill(this.L.annNowFree, { title: this.fragrance(freeAfterId).title });
      } else if (on) {
        message = fill(this.L.annSwitchedPlan, { title: product.title, price: this.fmt(variant.planPrice), every: variant.planEvery });
      } else {
        message = fill(this.L.annSwitchedOnce, { title: product.title, price: this.fmt(variant.price) });
      }
      this.armFreeMoment(freeBeforeId, freeAfterId);
      this.changed();
      // One live region per press: the switch, then the chosen card's own terms ("Then $39.99 every 30 days"), which
      // the pressed price card now carries (the separate note under the cards is gone, critique 2026-10-15).
      const pressed = card.querySelector('[data-se2-kit-plan][aria-pressed="true"] .se2-kit__pterms');
      const terms = pressed && !pressed.hidden ? pressed.textContent.trim() : '';
      const plain = (text) => text.replace(/\u00a0/g, ' ');
      this.say(terms && plain(message).indexOf(plain(terms)) < 0 ? message + ' ' + terms + '.' : message);
    }

    setSize(card, variantId) {
      if (!card) return;
      const id = card.getAttribute('data-product-id');
      const line = this.state.fragrances.find((l) => String(l.productId) === String(id));
      const product = this.fragrance(id);
      const variant = this.fvariant(product, variantId);
      if (!line || !variant || !variant.available || this.isCandidate(product)) return;
      this.beginEdit();
      line.variantId = variant.id;
      if (!variant.planId) line.plan = false;
      this.say(fill(this.L.annSizeChanged, { size: variant.title }));
      this.changed();
    }

    selectChip(key) {
      if (!key || key === this.state.chip) return;
      this.state.chip = key;
      this.save();
      this.renderFragrances({});
      // The list re-orders or filters silently otherwise: say how many fragrances it now shows.
      const shown = this.cards.filter((c) => !c.hidden).length;
      this.say(shown ? fill(shown === 1 ? this.L.countOne : this.L.countOther, { n: shown }) + '.' : this.L.noMatch);
    }

    /** The half of the Emotion / Scent toggle a chip belongs to ('emotion' | 'scent'), or '' for For you and All. */
    chipSetOf(key) {
      const chip = this.chips.find((c) => c.getAttribute('data-se2-kit-chip') === key);
      return (chip && chip.getAttribute('data-se2-kit-chipset')) || '';
    }

    /** The halves that have chips today. With fewer than two, the toggle hides and the row shows every chip. */
    chipSets() {
      const sets = new Set(this.chips.map((c) => c.getAttribute('data-se2-kit-chipset')).filter(Boolean));
      return ['emotion', 'scent'].filter((g) => sets.has(g));
    }

    /**
     * The Emotion / Scent toggle (critique 2026-10-15): shows one half of the chips, with For you or All in both. A
     * filter from the half being left would hide behind the toggle, so the list goes back to For you or All, through
     * selectChip, which saves and announces the count as before.
     */
    selectChipGroup(group) {
      if (!this.chipSets().includes(group) || group === this.chipGroup) return;
      this.chipGroup = group;
      const from = this.chipSetOf(this.state.chip);
      if (from && from !== group) this.selectChip(this.feelForRanking() ? 'for-you' : 'all');
      else this.renderFragrances({});
      if (this.chipRow) this.chipRow.scrollLeft = 0;
      this.updateChipNav();
    }

    /** Draws the toggle and which chips show: the chosen chip's half always wins, so a restored filter stays visible. */
    renderChipGroups() {
      const sets = this.chipSets();
      const grouped = Boolean(this.chipSeg) && sets.length > 1;
      if (this.chipSeg) this.chipSeg.hidden = !grouped;
      const own = this.chipSetOf(this.state.chip);
      if (!grouped) this.chipGroup = null;
      else if (own) this.chipGroup = own;
      else if (!sets.includes(this.chipGroup)) {
        const feel = this.feelForRanking();
        this.chipGroup = feel && feel.group === 'scent' ? 'scent' : 'emotion';
      }
      this.chipSetButtons.forEach((b) => b.setAttribute('aria-pressed', b.getAttribute('data-se2-kit-chipset-btn') === this.chipGroup ? 'true' : 'false'));
      if (this.chipRow) {
        if (this.chipGroup) this.chipRow.setAttribute('data-se2-kit-chipgroup', this.chipGroup);
        else this.chipRow.removeAttribute('data-se2-kit-chipgroup');
      }
      this.chips.forEach((chip) => {
        const set = chip.getAttribute('data-se2-kit-chipset');
        if (set) chip.hidden = Boolean(this.chipGroup) && set !== this.chipGroup;
      });
    }

    selectTab(tab, { user = false, silent = false } = {}) {
      if (tab !== 'diffusers' && tab !== 'fragrances') return;
      const from = this.state.tab;
      this.state.tab = tab;
      this.save();
      this.renderTabs(from !== tab && !silent ? (tab === 'fragrances' ? 1 : -1) : 0);
      // the bar's renew row depends on the tab (the offer's terms show while the Fragrances cards invite it)
      if (from !== tab) this.renderBar();
      if (user) {
        publish('se2_kit_tab', { tab });
        if (from !== tab && this.stuck) this.scrollPanelIntoView();
      }
    }

    /* ---- the quiz handoff (spec C1.6, C2.6) ---------------------------------------------------------------- */
    onQuizComplete(event) {
      const detail = (event && event.detail) || {};
      const s = this.state;
      let d = this.resolveDiffuser(detail.diffuser);
      if (!d) {
        for (const h of detail.candidates || []) {
          d = this.resolveDiffuser(h);
          if (d) break;
        }
      }
      this.beginEdit();
      if (d) s.diffuser = d.variant.id;
      const feel = detail.feel ? this.validFeel(detail.feel) : null;
      if (feel) {
        s.feel = { group: feel.group, key: feel.key, label: detail.feel.label || '' };
        s.chip = 'for-you';
        // The Emotion / Scent toggle opens on the half the answer was given in (renderChipGroups).
        this.chipGroup = null;
        const pick = this.topPick(s.feel);
        // A retaken quiz replaces its earlier pick (and a link's); hand-ticked fragrances stay as extras (C2.9).
        if (pick) this.pickFirst(s, pick, 'quiz');
      }
      const space = detail.space ? this.spaceFor(detail.space, detail.spaceLabel, detail.candidates || [detail.diffuser]) : null;
      if (space) s.space = space;
      s.tab = 'fragrances';
      this.normalize(false);
      this.pinChosen();
      this.cleanAddress();
      this.touch();
      this.save();
      this.renderAll({});
      this.quizHandoff = true;
      this.dispatchApplied('quiz');
    }

    onQuizClose(event) {
      const reason = event && event.detail && event.detail.reason;
      this.quizMemo = this.readQuiz();
      this.renderTitle();
      if (reason !== 'complete') return;
      if (this.title) this.title.focus({ preventScroll: true });
      requestAnimationFrame(() => {
        this.say(this.kitSentence(), true);
        // The preselected bottle's purchase toggle (the control that turns the subscription off) and its terms
        // come into view above the bar; the stuck tab bar keeps the kit on screen as the condensed stage.
        const first = this.state.fragrances[0];
        const card = first && this.state.tab === 'fragrances' ? this.cardFor(first.productId) : null;
        const row = card && !card.hidden ? card.querySelector('[data-se2-kit-purchase]') : null;
        if (row && !row.hidden) this.keepInView(row);
      });
    }

    /** The quiz's pick (index 0): the line the quiz result and the handoff sentence name. */
    firstLine() {
      const line = this.state.fragrances[0];
      if (!line) return null;
      const variant = this.lineVariant(line);
      const product = this.fragrance(line.productId);
      if (!variant || !product) return null;
      return { line, index: 0, variant, product, free: this.freeIndex() === 0 };
    }

    kitSentence() {
      const t = this.totals();
      const d = t.diffuser;
      const first = this.firstLine();
      if (!d) return '';
      if (!first) return fill(this.L.annKitDiffuser, { diffuser: d.product.title });
      const product = first.product;
      const v = first.variant;
      let out = fill(this.L.annKit, { diffuser: d.product.title, fragrance: product.title, size: v.title });
      if (first.free) out += ' ' + fill(this.L.annKitFree, { price: this.fmt(v.planPrice), every: v.planEvery });
      else if (first.line.plan) out += ' ' + fill(this.L.annKitPlan, { title: product.title, size: v.title, price: this.fmt(v.planPrice), every: v.planEvery });
      else out += ' ' + fill(this.L.annKitOnce, { title: product.title, size: v.title, price: this.fmt(v.price) });
      return out;
    }

    /** The free line, else the first line: the one the stage, the tab and the caption name. */
    leadLine() {
      const lines = this.state.fragrances;
      if (!lines.length) return null;
      const free = this.freeIndex();
      const index = free >= 0 ? free : 0;
      const line = lines[index];
      const variant = this.lineVariant(line);
      if (!variant) return null;
      return { line, index, variant, free: free === index && free >= 0, product: this.fragrance(line.productId) };
    }

    dispatchApplied(source) {
      const t = this.totals();
      // The pick itself (index 0), never an older free line: the quiz names the fragrance the shopper just chose.
      const first = this.firstLine();
      const detail = {
        source,
        diffuser: t.diffuser ? { handle: t.diffuser.product.handle, title: t.diffuser.product.title, variantId: t.diffuser.variant.id } : null,
        fragrance: first
          ? {
              handle: first.product.handle,
              title: first.product.title,
              size: first.variant.title,
              plan: Boolean(first.line.plan),
              free: first.free,
              planPrice: this.fmt(first.variant.planPrice),
              planEvery: first.variant.planEvery,
            }
          : null,
        tab: this.state.tab,
      };
      document.dispatchEvent(new CustomEvent('se2:kit:applied', { bubbles: true, detail }));
    }

    /* ---- rendering ---------------------------------------------------------------------------------------- */
    renderAll(opts) {
      this.renderTitle();
      this.renderOffer();
      this.renderStage(opts);
      this.renderTabs(0);
      this.renderDiffusers(opts);
      this.renderFragrances(opts);
      this.renderBar();
    }

    renderTitle() {
      if (!this.quizLink) return;
      const done = (this.quizMemo && this.quizMemo.status === 'done') || (this.state.feel && this.state.feel.key);
      const text = done ? this.L.quizAgain : this.L.quiz;
      if (text && this.quizLink.textContent !== text) this.quizLink.textContent = text;
    }

    renderOffer() {
      // The head shows the lede at all times (critique 2026-10-15), so only the root's flag follows the offer: off
      // when the cart refused the code this session, or when no fragrance can be sold (spec C2.8).
      if (!this.offerLive || !this.anySellableFragrance()) this.root.setAttribute('data-offer', 'off');
    }

    /** The empty state when no fragrance in the island can be sold: a restock line and a link, no list. */
    renderRestock() {
      const panel = this.panels.fragrances;
      if (!panel) return false;
      const none = !this.anySellableFragrance();
      let box = panel.querySelector('[data-se2-kit-restock]');
      if (none && !box) {
        box = el('div', 'se2-kit__restock');
        box.setAttribute('data-se2-kit-restock', '');
        box.appendChild(el('p', 'se2-kit__lead', this.L.restock));
        const a = el('a', 'se2-kit__textlink', this.L.restockLink);
        a.href = this.data.fragranceUrl || this.data.routes.collections;
        box.appendChild(a);
        panel.appendChild(box);
      }
      if (box) box.hidden = !none;
      const chips = panel.querySelector('[data-se2-kit-chips]');
      if (this.chipSeg && none) this.chipSeg.hidden = true;
      if (chips) chips.hidden = none;
      if (this.flist) this.flist.hidden = none;
      return none;
    }

    /* The stage: the diffuser and up to three bottles on one baseline, the halo of the lead bottle's family. */
    renderStage(opts = {}) {
      if (!this.stage) return;
      const t = this.totals();
      const d = t.diffuser;
      if (d) this.swapDiffuserImage(d.variant, opts.initial);
      const lines = this.state.fragrances;
      const free = t.free;
      // bottles
      // Three bottles fit; the free one is always among them (Codex review 2026-10-15): past the third, it takes the
      // third slot, so its Free tag and the free moment's rise always have a node.
      const picks = lines.slice(0, 3).map((line, i) => ({ line, i }));
      if (free >= 3 && lines[free]) picks[2] = { line: lines[free], i: free };
      const wanted = picks.map(({ line, i }) => ({ line, i, product: this.fragrance(line.productId) }));
      const existing = new Map(Array.from(this.bottles.querySelectorAll('[data-se2-kit-bottle]')).map((n) => [n.getAttribute('data-se2-kit-bottle'), n]));
      const nodes = [];
      wanted.forEach(({ line, i, product }) => {
        const key = String(line.productId);
        let node = existing.get(key);
        if (!node) {
          node = el('span', 'se2-kit__bottle');
          node.setAttribute('data-se2-kit-bottle', key);
          const img = el('img', 'se2-kit__bimg');
          img.alt = '';
          img.decoding = 'async';
          if (product.image) img.src = product.image;
          img.width = 240;
          img.height = 240;
          node.appendChild(img);
          const tag = el('span', 'se2-kit__btag', this.L.freeTag || 'Free');
          tag.setAttribute('data-se2-kit-btag', '');
          node.appendChild(tag);
          node.classList.add('is-new');
        }
        existing.delete(key);
        node.classList.toggle('is-free', i === free);
        nodes.push(node);
      });
      existing.forEach((node) => node.remove());
      let more = this.bottles.querySelector('[data-se2-kit-more]');
      nodes.forEach((node, i) => {
        if (this.bottles.children[i] !== node) this.bottles.insertBefore(node, this.bottles.children[i] || null);
      });
      if (lines.length > 3) {
        if (!more) {
          more = el('span', 'se2-kit__bmore');
          more.setAttribute('data-se2-kit-more', '');
        }
        more.textContent = '+' + (lines.length - 3);
        this.bottles.appendChild(more);
      } else if (more) {
        more.remove();
      }
      this.comp.style.setProperty('--se2-kit-n', String(Math.min(lines.length, 3) + (lines.length > 3 ? 0.6 : 0)));
      // The free moment's stage half: the free bottle rises in and its Free tag glints, once.
      const freeSlot = free >= 0 ? wanted.findIndex((w) => w.i === free) : -1;
      const freeNode = this.freeMoment && freeSlot >= 0 ? nodes[freeSlot] : null;
      if (freeNode) this.riseFree(freeNode);
      this.bottles.querySelectorAll('.is-new').forEach((node) => {
        requestAnimationFrame(() => requestAnimationFrame(() => node.classList.remove('is-new')));
      });
      // caption
      const cap1 = d ? d.product.title + ((d.product.variants || []).length > 1 ? ' · ' + d.variant.color : '') : '';
      const lead = this.leadLine();
      let cap2 = '';
      if (lead) {
        cap2 = lead.product.title + ' ' + lead.variant.title;
        if (lines.length > 1) cap2 += ' + ' + (lines.length - 1);
      }
      if (this.cap1.textContent !== cap1) this.cap1.textContent = cap1;
      if (this.cap2.textContent !== cap2) this.cap2.textContent = cap2;
      const items = [cap1].concat(lines.map((l) => {
        const p = this.fragrance(l.productId);
        const v = this.lineVariant(l);
        return p && v ? p.title + ' ' + v.title : '';
      })).filter(Boolean);
      this.stage.setAttribute('aria-label', fill(this.L.stage, { items: items.join('; ') }));
      // halo
      let slug = lead ? String(lead.product.familySlug || '') : '';
      if (slug === 'wood') slug = 'woody';
      this.setHalo(slug);
      // condensed stage
      this.renderMini(d, lines);
    }

    /**
     * The free bottle rises in by --se2-motion-rise and its one-word Free tag glints once (se2-plph-glint, one
     * pass), all from assets/se2-kit.css under prefers-reduced-motion: no-preference. The start state is committed
     * before the transition runs; the classes come off afterwards, so a later re-render (or the tag showing again
     * after it was hidden) never replays them.
     */
    riseFree(node) {
      node.classList.remove('is-new', 'is-rising', 'is-glint');
      node.classList.add('is-rise');
      void node.offsetWidth;
      requestAnimationFrame(() => {
        node.classList.remove('is-rise');
        node.classList.add('is-rising', 'is-glint');
        setTimeout(() => node.classList.remove('is-rising', 'is-glint'), 2400);
      });
    }

    /** One run of a CSS animation class on a node (the free moment's pop and tick); removed when it ends. */
    playOnce(node, className) {
      if (!node) return;
      node.classList.remove(className);
      void node.offsetWidth;
      node.classList.add(className);
      const done = () => node.classList.remove(className);
      node.addEventListener('animationend', done, { once: true });
      setTimeout(done, 1600);
    }

    swapDiffuserImage(variant, initial) {
      const front = this.dslot.querySelector('img.se2-kit__dimg.is-on');
      const src = variant.image;
      if (!src) return;
      if (front && front.getAttribute('src') === src) return;
      if (front && initial) {
        front.removeAttribute('srcset');
        front.src = src;
        if (variant.srcset) front.srcset = variant.srcset;
        return;
      }
      let back = this.dslot.querySelector('img.se2-kit__dimg:not(.is-on)');
      if (!back) {
        back = el('img', 'se2-kit__dimg');
        back.alt = '';
        back.decoding = 'async';
        back.setAttribute('sizes', '(min-width: 990px) 60vw, 90vw');
        this.dslot.appendChild(back);
      }
      back.width = variant.w || 1;
      back.height = variant.h || 1;
      back.removeAttribute('srcset');
      back.src = src;
      if (variant.srcset) back.srcset = variant.srcset;
      const token = (this.swapToken = (this.swapToken || 0) + 1);
      const show = () => {
        if (token !== this.swapToken) return;
        back.classList.add('is-on');
        if (front) front.classList.remove('is-on');
      };
      (back.decode ? back.decode() : Promise.resolve()).then(show, show);
    }

    setHalo(slug) {
      if (this.halo === slug) return;
      this.halo = slug;
      const [a, b] = this.halos;
      if (!a || !b) return;
      const on = a.classList.contains('is-on') ? a : b;
      const off = on === a ? b : a;
      off.style.setProperty('--se2-kit-halo', slug ? 'var(--se2-halo-' + slug + ')' : 'none');
      off.classList.add('is-on');
      on.classList.remove('is-on');
    }

    renderMini(d, lines) {
      if (!this.mini) return;
      if (d && this.miniD) {
        const img = this.miniD.querySelector('img') || el('img');
        if (!img.parentNode) {
          img.alt = '';
          img.width = 40;
          img.height = 40;
          this.miniD.appendChild(img);
        }
        if (d.variant.thumb && img.getAttribute('src') !== d.variant.thumb) img.src = d.variant.thumb;
      }
      if (this.miniB) {
        const want = lines.slice(0, 3).map((l) => String(l.productId)).join(',');
        if (this.miniB.getAttribute('data-ids') !== want) {
          this.miniB.setAttribute('data-ids', want);
          this.miniB.textContent = '';
          lines.slice(0, 3).forEach((l) => {
            const p = this.fragrance(l.productId);
            const chip = el('span', 'se2-kit__minichip');
            chip.setAttribute('data-se2-kit-minichip', String(l.productId));
            if (p && p.image) {
              const img = el('img');
              img.src = p.image;
              img.alt = '';
              img.width = 28;
              img.height = 28;
              chip.appendChild(img);
            }
            this.miniB.appendChild(chip);
          });
        }
      }
    }

    renderTabs(direction) {
      const s = this.state;
      this.root.setAttribute('data-se2-kit-tab', s.tab);
      const t = this.totals();
      const lead = this.leadLine();
      const subs = {
        diffusers: t.diffuser ? t.diffuser.product.title : this.L.chooseOne,
        fragrances: lead ? lead.product.title : this.L.chooseOne,
      };
      const more = lead && s.fragrances.length > 1 ? ' + ' + (s.fragrances.length - 1) : '';
      this.tabButtons.forEach((button) => {
        const key = button.getAttribute('data-se2-kit-tab-btn');
        const selected = key === s.tab;
        button.setAttribute('aria-selected', selected ? 'true' : 'false');
        button.tabIndex = selected ? 0 : -1;
        const sub = button.querySelector('[data-se2-kit-tabsub]');
        const name = key === 'diffusers' ? this.L.tabDiffusers : this.L.tabFragrances;
        if (key === 'fragrances') {
          // The lead bottle's name and the count, with no Free tag (critique 2026-10-15: the stage, the price card
          // and the bar already say it in this view).
          const text = subs.fragrances + more;
          if (sub.textContent !== text) sub.textContent = text;
          button.setAttribute('aria-label', name + ': ' + (lead ? lead.product.title + more : this.L.chooseOne));
        } else {
          sub.textContent = subs.diffusers;
          button.setAttribute('aria-label', name + ': ' + subs.diffusers);
        }
      });
      Object.entries(this.panels).forEach(([key, panel]) => {
        if (!panel) return;
        const show = key === s.tab;
        if (panel.hidden === !show) return;
        panel.hidden = !show;
        if (show && direction && !reduced() && panel.animate) {
          panel.animate(
            [
              { opacity: 0, transform: 'translateX(' + 8 * direction + 'px)' },
              { opacity: 1, transform: 'none' },
            ],
            { duration: 180, easing: EASE }
          );
        }
      });
      this.placeTabIndicator();
    }

    placeTabIndicator() {
      if (!this.tabInd) return;
      const selected = this.tabButtons.find((b) => b.getAttribute('aria-selected') === 'true');
      if (!selected) return;
      this.tabInd.style.width = selected.offsetWidth + 'px';
      this.tabInd.style.transform = 'translateX(' + selected.offsetLeft + 'px)';
    }

    renderDiffusers(opts = {}) {
      const t = this.totals();
      const d = t.diffuser;
      const space = this.state.space;
      const chosenPid = d ? String(d.product.productId) : '';
      this.rows.forEach((row) => {
        const pid = row.getAttribute('data-product-id');
        const product = this.diffuserByProduct(pid);
        const chosen = pid === chosenPid;
        row.classList.toggle('is-chosen', chosen);
        const input = row.querySelector('input[name="se2-kit-diffuser"]');
        if (input) input.checked = chosen;
        if (chosen) row.setAttribute('data-variant-id', String(d.variant.id));
        // tags
        const tags = row.querySelector('[data-se2-kit-dtags]');
        const handle = row.getAttribute('data-handle');
        let tag = '';
        if (space && space.candidates && space.candidates.length) {
          if (space.candidates[0] === handle) tag = this.L.spaceTag;
          else if (space.candidates.indexOf(handle) > 0) tag = this.L.spaceAlt;
        }
        if (tags) {
          const current = tags.textContent;
          if (current !== tag) {
            tags.textContent = '';
            if (tag) tags.appendChild(el('span', 'se2-kit__tag', tag));
          }
        }
        // plug note
        const plug = row.querySelector('[data-se2-kit-plug]');
        if (plug) {
          const show = Boolean(space && space.key === 'intimate' && product && product.plugIn);
          plug.hidden = !show;
          plug.textContent = show ? this.L.plugNote : '';
        }
        // colours
        const colors = row.querySelector('[data-se2-kit-colors]');
        if (colors) this.renderColors(colors, chosen ? d : null);
      });
      // A sold-out band's alternative button goes once that diffuser is the chosen one.
      this.altButtons.forEach((b) => {
        b.hidden = b.getAttribute('data-se2-kit-alt') === chosenPid;
      });
      this.groups.forEach((group) => {
        const has = Boolean(group.querySelector('[data-se2-kit-diffuser].is-chosen'));
        group.classList.toggle('is-chosen', has);
        const features = group.querySelector('[data-se2-kit-features]');
        if (features) features.hidden = !has;
      });
      this.renderSpecs(d ? d.product : null, opts.initial);
    }

    renderColors(box, d) {
      const variants = d ? (d.product.variants || []) : [];
      if (!d || variants.length < 2) {
        if (box.childNodes.length) box.textContent = '';
        box.hidden = true;
        return;
      }
      box.hidden = false;
      const sig = d.product.productId + ':' + d.variant.id;
      if (box.getAttribute('data-sig') === sig) return;
      const hadFocus = box.contains(document.activeElement);
      box.setAttribute('data-sig', sig);
      box.textContent = '';
      const fs = el('fieldset', 'se2-kit__colorset');
      const legend = el('legend', 'se2-kit__colorlabel');
      legend.appendChild(document.createTextNode(this.L.color + ': '));
      legend.appendChild(el('span', 'se2-kit__colorname', d.variant.color));
      fs.appendChild(legend);
      const list = el('div', 'se2-kit__swatches');
      variants.forEach((v) => {
        const label = el('label', 'se2-kit__swatch' + (v.available ? '' : ' is-soldout'));
        const input = el('input', 'se2-kit__swatchinput');
        input.type = 'radio';
        input.name = 'se2-kit-color';
        input.value = String(v.id);
        input.checked = String(v.id) === String(d.variant.id);
        input.disabled = !v.available;
        input.setAttribute('aria-label', v.color + (v.available ? '' : ', ' + this.L.soldOut));
        const dot = el('span', 'se2-kit__swatchdot');
        dot.style.setProperty('--se2-kit-swatch', v.swatch || 'var(--se2-finish-silver)');
        dot.setAttribute('aria-hidden', 'true');
        label.appendChild(input);
        label.appendChild(dot);
        list.appendChild(label);
      });
      fs.appendChild(list);
      box.appendChild(fs);
      if (hadFocus) {
        const checked = box.querySelector('input:checked');
        if (checked) checked.focus();
      }
    }

    renderSpecs(product, initial) {
      if (!this.specs || !product) return;
      const holdsParts = String(product.holds || '').split(' ');
      const values = {
        coverage: { k: product.coverageLabel, n: product.coverageValue, u: product.coverageValue ? ' sq. ft.' : '' },
        holds: { k: this.L.holds || 'Holds', n: holdsParts[0] || '', u: holdsParts.length > 1 ? ' ' + holdsParts.slice(1).join(' ') : '' },
        control: { k: this.L.control || 'Control', n: product.control || '', u: '' },
      };
      Object.entries(values).forEach(([key, value]) => {
        const cell = this.specs.querySelector('[data-se2-kit-spec="' + key + '"]');
        if (!cell) return;
        cell.hidden = !value.n;
        const k = cell.querySelector('[data-k]');
        const v = cell.querySelector('[data-v]');
        if (k && k.textContent !== value.k) k.textContent = value.k;
        const n = v.querySelector('.se2-kit__specn');
        const u = v.querySelector('.se2-kit__specu');
        const changed = n.textContent !== value.n || (u ? u.textContent : '') !== value.u;
        if (!changed) return;
        n.textContent = value.n;
        if (u) u.textContent = value.u;
        else if (value.u) v.appendChild(el('span', 'se2-kit__specu', value.u));
        if (!initial && !reduced() && v.animate) {
          v.animate(
            [
              { opacity: 0, transform: 'translateY(6px)' },
              { opacity: 1, transform: 'none' },
            ],
            { duration: 180, easing: EASE }
          );
        }
      });
    }

    /* The FRAGRANCES panel: chips, order, cards, purchase rows. */
    renderFragrances(opts = {}) {
      if (this.renderRestock()) return;
      const s = this.state;
      const feel = this.feelForRanking();
      // chips
      const forYou = this.chips.find((c) => c.getAttribute('data-se2-kit-chip') === 'for-you');
      const all = this.chips.find((c) => c.getAttribute('data-se2-kit-chip') === 'all');
      if (forYou) forYou.hidden = !feel;
      if (all) all.hidden = Boolean(feel);
      if (s.chip === 'for-you' && !feel) s.chip = 'all';
      if (s.chip === 'all' && feel) s.chip = 'for-you';
      if (!this.chips.some((c) => c.getAttribute('data-se2-kit-chip') === s.chip)) s.chip = feel ? 'for-you' : 'all';
      this.chips.forEach((chip) => {
        const on = chip.getAttribute('data-se2-kit-chip') === s.chip;
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
        chip.classList.toggle('is-active', on);
      });
      this.renderChipGroups();
      this.orderList(feel);
      // cards
      const lines = s.fragrances;
      const free = this.freeIndex();
      // After an add whose bottle the cart charged, no unchosen card invites "Free on Subscribe & save" either.
      const noFreeYet = free < 0 && !this.offerSpent();
      const atMax = lines.length >= this.max;
      this.cards.forEach((card) => this.renderCard(card, { lines, free, noFreeYet, atMax }));
      // "Add a second…" under the first chosen card
      let extra = this.root.querySelector('[data-se2-kit-extra]');
      if (lines.length >= 1 && lines.length < this.max) {
        const firstCard = this.cardFor(lines[0].productId);
        if (!extra) {
          extra = el('p', 'se2-kit__extra', this.L.extraPrompt);
          extra.setAttribute('data-se2-kit-extra', '');
        }
        if (firstCard && !firstCard.hidden) {
          if (firstCard.nextElementSibling !== extra) firstCard.after(extra);
          extra.hidden = false;
        } else {
          extra.hidden = true;
        }
      } else if (extra) {
        extra.remove();
      }
      this.updateChipNav();
    }

    /** The chip row's paging buttons (shown at 990 and up by CSS): each only while the row has more that way. */
    updateChipNav() {
      const row = this.chipRow;
      if (!row || !this.chipNav.length) return;
      const max = row.scrollWidth - row.clientWidth;
      this.chipNav.forEach((b) => {
        const dir = Number(b.getAttribute('data-se2-kit-chipnav'));
        b.hidden = max <= 1 || (dir < 0 ? row.scrollLeft <= 1 : row.scrollLeft >= max - 1);
      });
    }

    pageChips(dir) {
      const row = this.chipRow;
      if (!row) return;
      row.scrollBy({ left: dir * Math.max(120, row.clientWidth - 96), behavior: reduced() ? 'auto' : 'smooth' });
    }

    cardFor(productId) {
      return this.cards.find((c) => c.getAttribute('data-product-id') === String(productId)) || null;
    }

    /** Records the chosen fragrances, in choice order, as the ones the "All" list shows first (KV-02). */
    pinChosen() {
      this.pinned = this.state.fragrances.map((l) => String(l.productId));
      this.linkPinned = this.state.fragrances.filter((l) => l.src === 'url').map((l) => String(l.productId));
    }

    orderList(feel) {
      if (!this.flist) return;
      const chip = this.state.chip;
      const chosenIds = new Set(this.state.fragrances.map((l) => String(l.productId)));
      const sig =
        chip + '|' + (feel ? feel.group + ':' + feel.key : '') + '|' + this.pinned.join(',') + '|' + this.linkPinned.join(',') +
        '|' + (this.showAll ? 'all' : 'fold') + '|' + Array.from(chosenIds).join(',');
      if (sig === this.listSignature) return;
      this.listSignature = sig;
      const byId = (f) => this.cardFor(f.id);
      let order = this.fragrances;
      // head: cards above the "Your matches" eyebrow; split: the index where "More fragrances" starts.
      let head = 0;
      let split = -1;
      let visible = null;
      if (chip === 'for-you' && feel) {
        const ranked = this.rank(feel);
        if (ranked) {
          // A link's pick (?scent=) leads "For you" too, so the linked fragrance and its purchase row are the first
          // card even when a stored quiz answer opens this chip (KV-02). A pick the answer also matches leads
          // "Your matches"; one it does not match sits above that eyebrow, never under a heading that would call
          // it a match. The quiz's own pick is its top match already, so a quiz hand-off reads as before.
          const lead = this.linkPinned.map((id) => this.fragrances.find((f) => String(f.id) === id)).filter(Boolean);
          const leadIds = new Set(lead.map((f) => String(f.id)));
          const isLead = (f) => leadIds.has(String(f.id));
          const above = lead.filter((f) => !ranked.matches.includes(f));
          const matches = lead.filter((f) => ranked.matches.includes(f)).concat(ranked.matches.filter((f) => !isLead(f)));
          order = above.concat(matches, ranked.more.filter((f) => !isLead(f)));
          head = above.length;
          split = head + matches.length;
        }
      } else if (chip.indexOf(':') > 0) {
        const [kind, key] = chip.split(':');
        visible = new Set(
          this.fragrances.filter((f) => ((kind === 'mood' ? f.moods : f.families) || []).includes(key)).map((f) => String(f.id))
        );
      } else if (chip === 'all' && this.pinned.length) {
        // An arrival's pick sits at the top of "All", so a linked bottle and its purchase row are the first card,
        // the way "Your matches" leads after the quiz. The rest keep the collection order.
        const pins = this.pinned.map((id) => this.fragrances.find((f) => String(f.id) === id)).filter(Boolean);
        order = pins.concat(this.fragrances.filter((f) => !this.pinned.includes(String(f.id))));
      }
      // The fold (owner, 2026-10-15: on a phone the list ran sixteen cards long). Without a feeling chip, the list
      // shows the quiz's matches (or the first FOLD cards) and one button for the rest; a chosen card is always shown,
      // and a chip's filter always shows every card it matches.
      const FOLD = 4;
      const limit = visible || this.showAll ? Infinity : split > head ? split : Math.max(FOLD, head);
      let folded = 0;
      const shows = (f, i) => i < limit || chosenIds.has(String(f.id));
      const anyAfterSplit = split >= 0 && order.some((f, i) => i >= split && shows(f, i));
      const frag = document.createDocumentFragment();
      if (this.eyebrows.matches) this.eyebrows.matches.hidden = !(split > head);
      order.forEach((f, i) => {
        if (split > head && i === head && this.eyebrows.matches) frag.appendChild(this.eyebrows.matches);
        if (split >= 0 && i === split && this.eyebrows.more) {
          this.eyebrows.more.hidden = order.length === split || (!visible && !anyAfterSplit);
          frag.appendChild(this.eyebrows.more);
        }
        const card = byId(f);
        if (!card) return;
        const fold = !shows(f, i);
        if (fold) folded += 1;
        card.hidden = visible ? !visible.has(String(f.id)) : fold;
        frag.appendChild(card);
      });
      if ((split < 0 || split >= order.length) && this.eyebrows.more) this.eyebrows.more.hidden = true;
      if (!this.moreBtn) {
        this.moreBtn = el('button', 'se2-btn se2-btn--secondary se2-kit__fmore');
        this.moreBtn.type = 'button';
        this.moreBtn.setAttribute('data-se2-kit-showall', '');
      }
      this.moreBtn.hidden = folded === 0;
      if (folded) this.moreBtn.textContent = fill(this.L.showMore || 'Show [count] more', { count: folded });
      frag.appendChild(this.moreBtn);
      if (this.noMatch) {
        this.noMatch.hidden = !(visible && visible.size === 0);
        frag.appendChild(this.noMatch);
      }
      const active = document.activeElement;
      const keep = active && this.flist.contains(active) ? active : null;
      this.flist.appendChild(frag);
      if (keep && document.activeElement !== keep) keep.focus({ preventScroll: true });
    }

    /**
     * The card's price line: the price and the size. A line may break only before a separator: the "·" is glued to
     * the part after it by a no-break space, and every part after the first is nowrap in CSS, so "120 mL" stays whole.
     */
    priceLine(parts) {
      const frag = document.createDocumentFragment();
      parts.forEach((part, i) => {
        if (i > 0) {
          const sep = el('span', 'se2-kit__psep', ' ·\u00a0');
          sep.setAttribute('aria-hidden', 'true');
          frag.appendChild(sep);
        }
        frag.appendChild(el('span', part.cls || '', nb(part.text)));
      });
      return frag;
    }

    renderCard(card, ctx) {
      const product = this.fragrance(card.getAttribute('data-product-id'));
      if (!product) return;
      const index = ctx.lines.findIndex((l) => String(l.productId) === String(product.id));
      const chosen = index >= 0;
      const line = chosen ? ctx.lines[index] : null;
      const candidate = this.isCandidate(product);
      const isFree = chosen && index === ctx.free;
      const L = this.L;
      const offerSize = (this.data.offer && this.data.offer.size) || '';
      card.classList.toggle('is-chosen', chosen);
      card.toggleAttribute('data-first', index === 0);
      card.toggleAttribute('data-free', isFree);
      const blocked = !product.available || (!chosen && ctx.atMax);
      if (blocked) card.setAttribute('aria-disabled', 'true');
      else card.removeAttribute('aria-disabled');
      const check = card.querySelector('.se2-kit__check');
      if (check) {
        check.checked = chosen;
        check.disabled = blocked;
      }
      // price line: the selected size and its price, the plan price on Subscribe & save. The offer is said by the
      // price cards under a chosen bottle, not here (critique 2026-10-15), and no price is ever struck through.
      const priceEl = card.querySelector('[data-se2-kit-fprice]');
      const variant = chosen ? this.lineVariant(line) : this.fvariant(product, this.addVariantFor(product));
      let parts;
      if (!product.available || !variant) {
        parts = [{ text: L.soldOut, cls: 'se2-kit__pstrong' }];
      } else if (chosen && line.plan && variant.planId) {
        parts = [{ text: this.fmt(variant.planPrice), cls: 'se2-kit__pstrong' }, { text: variant.title }];
      } else {
        parts = [{ text: this.fmt(variant.price), cls: 'se2-kit__pstrong' }, { text: variant.title }];
      }
      parts = parts.filter((p) => p.text && p.text !== 'Default Title');
      const sig = JSON.stringify(parts);
      if (priceEl && priceEl.getAttribute('data-sig') !== sig) {
        priceEl.setAttribute('data-sig', sig);
        priceEl.textContent = '';
        priceEl.appendChild(this.priceLine(parts));
      }
      // caption
      const cap = card.querySelector('[data-se2-kit-fcap]');
      let capText = '';
      if (!chosen && ctx.atMax && product.available) capText = fill(L.maxReached, { n: this.max });
      else if (!chosen && product.available && this.offerLive && ctx.noFreeYet && !candidate) capText = fill(L.notInSize, { size: offerSize });
      if (cap) {
        cap.hidden = !capText;
        if (cap.textContent !== capText) cap.textContent = capText;
      }
      // purchase row
      const row = card.querySelector('[data-se2-kit-purchase]');
      if (!row) return;
      if (!chosen || !variant) {
        if (!row.hidden) {
          row.hidden = true;
          row.textContent = '';
          row.style.height = '';
        }
        return;
      }
      this.renderPurchase(row, product, line, variant, { isFree, candidate, noFreeYet: ctx.noFreeYet });
      row.hidden = false;
    }

    renderPurchase(row, product, line, variant, info) {
      const L = this.L;
      // sizes: only a non-candidate with two or more sellable sizes
      const sellable = (product.variants || []).filter((v) => v.available);
      let sizes = row.querySelector('[data-se2-kit-sizes]');
      if (!info.candidate && sellable.length > 1) {
        if (!sizes) {
          sizes = el('div', 'se2-kit__sizes');
          sizes.setAttribute('data-se2-kit-sizes', '');
          sizes.setAttribute('role', 'group');
          sizes.setAttribute('aria-label', product.title + ': size');
          row.prepend(sizes);
        }
        const sig = sellable.map((v) => v.id).join(',') + ':' + variant.id;
        if (sizes.getAttribute('data-sig') !== sig) {
          sizes.setAttribute('data-sig', sig);
          sizes.textContent = '';
          sellable.forEach((v) => {
            const b = el('button', 'se2-kit__size', v.title);
            b.type = 'button';
            b.setAttribute('data-se2-kit-size', String(v.id));
            b.setAttribute('aria-pressed', String(v.id) === String(variant.id) ? 'true' : 'false');
            sizes.appendChild(b);
          });
        }
      } else if (sizes) {
        sizes.remove();
      }
      // The two price cards (design 2026-10-13; critique 2026-10-15). Each card names its option, its price and its
      // own terms, so the choice reads without a note under them: the separate plan note is gone.
      let toggle = row.querySelector('[data-testid="se2-kit-plan-toggle"]');
      if (variant.planId) {
        if (!toggle) {
          toggle = el('div', 'se2-kit__plan');
          toggle.setAttribute('role', 'group');
          toggle.setAttribute('data-testid', 'se2-kit-plan-toggle');
          const opts = el('div', 'se2-kit__popts');
          ['on', 'off'].forEach((key) => {
            const b = el('button', 'se2-kit__popt');
            b.type = 'button';
            b.setAttribute('data-se2-kit-plan', key);
            b.appendChild(el('span', 'se2-kit__ptitle'));
            b.appendChild(el('span', 'se2-kit__pprice'));
            b.appendChild(el('span', 'se2-kit__pterms'));
            opts.appendChild(b);
          });
          toggle.appendChild(opts);
          const sizesEl = row.querySelector('[data-se2-kit-sizes]');
          if (sizesEl) sizesEl.after(toggle);
          else row.prepend(toggle);
        }
        toggle.setAttribute('aria-label', fill(L.purchaseGroup, { title: product.title }));
        const [on, off] = toggle.querySelectorAll('[data-se2-kit-plan]');
        const planPrice = this.fmt(variant.planPrice);
        const setText = (node, text) => {
          if (node.textContent !== text) node.textContent = text;
          node.hidden = !text;
        };
        // The one-subscription cap (owner, 2026-10-13): another bottle holds the kit's plan, so this Subscribe card
        // is locked and says why in its own terms line (plan_taken), where the note used to.
        const holder = !line.plan ? this.planHolder(line) : null;
        const other = holder ? this.fragrance(holder.productId) : null;
        // Free-eligible: the bottle the offer frees, or the one it would free on Subscribe & save. Its card reads
        // "Subscribe & save" (no percentage), "Free today", "Then $39.99 every 30 days": free with its condition and
        // renewal in the same card (VOICE.md section 4). Any other plan card shows the percentage, price and cadence.
        const eligible = Boolean(L.planFreeToday) && (info.isFree || (info.candidate && !line.plan && info.noFreeYet && !holder));
        const subText = eligible
          ? L.subscribeOffer || L.subscribe
          : fill(L.subscribe, { percent: variant.planPercent || this.data.planPercent || '' }).replace(/\s+%/, '%');
        let terms = variant.planEvery || '';
        if (eligible) terms = fill(L.thenPrice, { price: planPrice, every: variant.planEvery });
        else if (holder && L.planTaken) terms = fill(L.planTaken, { title: other ? other.title : '' });
        setText(on.querySelector('.se2-kit__ptitle'), nb(subText));
        setText(on.querySelector('.se2-kit__pprice'), eligible ? L.planFreeToday : planPrice);
        setText(on.querySelector('.se2-kit__pterms'), nb(terms));
        // One-time gets a second line, so the two cards match in height (critique 2026-10-15).
        setText(off.querySelector('.se2-kit__ptitle'), L.once);
        setText(off.querySelector('.se2-kit__pprice'), this.fmt(variant.price));
        setText(off.querySelector('.se2-kit__pterms'), L.billedOnce || '');
        on.setAttribute('aria-pressed', line.plan ? 'true' : 'false');
        off.setAttribute('aria-pressed', line.plan ? 'false' : 'true');
        on.classList.toggle('is-locked', Boolean(holder));
        if (holder) on.setAttribute('aria-disabled', 'true');
        else on.removeAttribute('aria-disabled');
        // The free moment's price-card half: "Free today" pops once (se2-kit-pop) as the bottle turns free.
        if (this.freeMoment && info.isFree) this.playOnce(on.querySelector('.se2-kit__pprice'), 'is-pop');
      } else if (toggle) {
        toggle.remove();
      }
    }

    /** Opens a chosen card's purchase row with a height transition and keeps it above the bar. */
    revealPurchase(card) {
      const row = card && card.querySelector('[data-se2-kit-purchase]');
      if (!row || row.hidden) return;
      const target = row.scrollHeight;
      if (!reduced() && row.animate) {
        row.animate([{ height: '0px', opacity: 0 }, { height: target + 'px', opacity: 1 }], { duration: 200, easing: EASE });
      }
      this.keepInView(row, target);
    }

    keepInView(node, finalHeight) {
      const rect = node.getBoundingClientRect();
      const height = finalHeight || rect.height;
      const barTop = this.bar ? this.bar.getBoundingClientRect().top : window.innerHeight;
      // Once scrolled, the tab bar sticks (at 0 below 990, where the header hides; at 96 above), so that is the
      // highest a row may end up.
      const tabHeight = this.tabbar ? this.tabbar.getBoundingClientRect().height : 0;
      const topLimit = (narrow() ? 0 : 96) + tabHeight + 8;
      const limit = barTop - 12;
      let delta = 0;
      if (rect.top + height > limit) delta = rect.top + height - limit;
      if (rect.top - delta < topLimit) delta = rect.top - topLimit;
      if (Math.abs(delta) < 1) return;
      const behavior = reduced() ? 'instant' : 'smooth';
      const scroller = narrow() ? window : document.querySelector('.page-wrapper') || window;
      scroller.scrollBy({ top: delta, behavior });
    }

    cardImageRect(card) {
      const img = card && card.querySelector('.se2-kit__fmedia img');
      return img ? img.getBoundingClientRect() : null;
    }

    inView(node) {
      if (!node) return false;
      const r = node.getBoundingClientRect();
      return r.width > 0 && r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth;
    }

    /** Fly-to-stage: the card's picture travels to its bottle slot (or the condensed chip), 420ms. */
    flyToStage(card, product, fromRect) {
      if (reduced() || !fromRect || !product || !product.image || !document.body.animate) return;
      const bottle = this.bottles.querySelector('[data-se2-kit-bottle="' + product.id + '"]');
      const chip = this.miniB && this.miniB.querySelector('[data-se2-kit-minichip="' + product.id + '"]');
      let target = null;
      if (bottle && this.inView(this.stage)) target = bottle;
      else if (chip && this.stuck && this.inView(chip)) target = chip;
      if (!target) return;
      const to = target.getBoundingClientRect();
      if (!to.width || !fromRect.width) return;
      const clone = el('img', 'se2-kit__fly');
      clone.src = (card.querySelector('.se2-kit__fmedia img') || {}).currentSrc || product.image;
      clone.alt = '';
      Object.assign(clone.style, {
        left: fromRect.left + 'px',
        top: fromRect.top + 'px',
        width: fromRect.width + 'px',
        height: fromRect.height + 'px',
      });
      this.root.appendChild(clone);
      target.classList.add('is-landing');
      const dx = to.left + to.width / 2 - (fromRect.left + fromRect.width / 2);
      const dy = to.top + to.height / 2 - (fromRect.top + fromRect.height / 2);
      const scale = Math.max(0.2, Math.min(to.width / fromRect.width, to.height / fromRect.height));
      const anim = clone.animate(
        [
          { transform: 'translate(0, 0) scale(1)', opacity: 1 },
          { transform: 'translate(' + dx + 'px, ' + dy + 'px) scale(' + scale + ')', opacity: 0.9 },
        ],
        { duration: 420, easing: EASE }
      );
      const done = () => {
        clone.remove();
        target.classList.remove('is-landing');
      };
      anim.onfinish = done;
      anim.oncancel = done;
    }

    /* The bar: notice, renew row, total, the one button. */
    renderBar() {
      if (!this.bar) return;
      const L = this.L;
      const t = this.totals();
      const lines = this.state.fragrances;
      // renew row
      let renew = '';
      if (t.anyPlan) {
        const tokens = { price: this.fmt(t.recurring), every: t.cadence || '' };
        // The free bottle's terms in one compact line beside the CTA and its Terms link (critique 2026-10-15):
        // "Free today on Subscribe & save, then $39.99 every 30 days. Cancel anytime."
        if (t.free >= 0 && t.cadence !== null) renew = fill(L.barRenewFreeCompact, tokens);
        else if (t.cadence !== null) {
          const count = lines.filter((l) => l.plan).length;
          tokens.count = fill(count === 1 ? L.countOne : L.countOther, { n: count });
          renew = fill(L.barRenewPlan, tokens);
        } else renew = L.barRenewMixed;
      } else if (this.offerInvites()) {
        // Before a fragrance oil is free, on the Fragrances tab, the bar says the offer with its renewal price, the
        // cadence and the cancel terms (VOICE §4, spec B.1): the one place this view says it until a bottle is
        // chosen, when its Subscribe & save price card takes over. The Diffusers tab keeps its bar free of it (spec
        // B.1 as built; the head line no longer carries the offer since the critique of 2026-10-15).
        const o = this.data.offer || {};
        renew = fill(L.barRenewOffer, { price: this.fmt(o.planCents || 0), every: o.planEvery || '' });
      }
      // "then" stays joined to the renewal it introduces, so no line ends on it (snippets/se2-kit-offer.liquid rule).
      renew = nb(renew).replace(/ then /g, ' then\u00a0');
      if (this.renew) {
        this.renew.hidden = !renew;
        if (this.renewText.textContent !== renew) this.renewText.textContent = renew;
      }
      // total: while the kit sits in the cart as configured, what the cart charges for it
      const fig = this.fmt(t.cartDue != null ? t.cartDue : t.due);
      // The free moment's bar half: the total takes the shared cart tick (se2-tick, assets/se2-motion.css) instead
      // of its usual slide, once, as the bottle turns free.
      const tick = this.freeMoment && t.free >= 0;
      if (this.totalFig.textContent !== fig) {
        this.totalFig.textContent = fig;
        if (!tick && !reduced() && this.totalFig.animate && this.booted) {
          this.totalFig.animate([{ opacity: 0.2, transform: 'translateY(4px)' }, { opacity: 1, transform: 'none' }], { duration: 180, easing: EASE });
        }
      }
      if (tick) this.playOnce(this.totalFig, 'se2-tick');
      const label = t.anyPlan ? L.barDue : L.barLabel;
      if (this.totalLabel.textContent !== label) this.totalLabel.textContent = label;
      // the button
      const state = this.ctaState();
      const text =
        state === 'need-diffuser'
          ? L.ctaNeedDiffuser
          : state === 'need-fragrance'
            ? L.ctaNeedFragrance
            : state === 'diffuser-only'
              ? fill(L.ctaDiffuserOnly, { diffuser: t.diffuser ? t.diffuser.product.title : '' })
              : state === 'adding'
                ? L.ctaAdding
                : state === 'added'
                  ? L.ctaViewCart
                  : this.addedOnce && L.ctaAddAgain
                    ? L.ctaAddAgain
                    : L.ctaAdd;
      if (this.cta.textContent !== text) this.cta.textContent = text;
      const was = this.cta.getAttribute('data-state');
      this.cta.setAttribute('data-state', state);
      if (state === 'adding') this.cta.setAttribute('aria-busy', 'true');
      else this.cta.removeAttribute('aria-busy');
      if (was && was !== state && state === 'add' && !reduced() && this.cta.animate) {
        this.cta.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.02)' }, { transform: 'scale(1)' }], { duration: 300, easing: EASE });
      }
      this.booted = true;
      this.fitBar();
    }

    /** The Fragrances list is inviting a free first fragrance oil: the offer is live, no line is free, a candidate shows. */
    offerInvites() {
      if (!this.offerLive || this.state.tab !== 'fragrances' || this.offerSpent()) return false;
      if (this.freeIndex() >= 0) return false;
      const chosen = new Set(this.state.fragrances.map((l) => String(l.productId)));
      return this.fragrances.some((f) => f.available && this.isCandidate(f) && !chosen.has(String(f.id)));
    }

    ctaState() {
      const t = this.totals();
      if (this.busy) return 'adding';
      if (this.added) return 'added';
      if (!t.diffuser) return 'need-diffuser';
      if (!this.anySellableFragrance()) return 'diffuser-only';
      if (!this.state.fragrances.length) return 'need-fragrance';
      return 'add';
    }

    fitBar() {
      if (!this.bar) return;
      const h = Math.round(this.bar.getBoundingClientRect().height);
      if (h > 0) this.root.style.setProperty('--se2-kit-bar-measured', h + 'px');
    }

    /**
     * OD2's "visible without extra taps": the free bottle's renew line (the compact form since the critique of
     * 2026-10-15) must fit the renew row in two lines at the current width; if it needs three, new choices join
     * One-time (spec C2.10).
     */
    measureRenewFit() {
      if (!this.bar || !this.renew) return;
      const inner = this.bar.querySelector('.se2-kit__barin');
      const width = inner ? inner.clientWidth : this.bar.clientWidth;
      if (!width) return;
      const o = this.data.offer || {};
      const tokens = { price: this.fmt(o.planCents || 0), every: o.planEvery || '' };
      const probe = el('p', 'se2-kit__renew se2-kit__measure');
      probe.setAttribute('aria-hidden', 'true');
      probe.style.width = width + 'px';
      const terms = this.renew.querySelector('.se2-kit__terms');
      const lines = (text) => {
        probe.textContent = nb(text).replace(/ then /g, ' then\u00a0') + (terms ? ' ' + terms.textContent.trim() : '');
        const lh = parseFloat(getComputedStyle(probe).lineHeight) || 16;
        return Math.round(probe.getBoundingClientRect().height / lh);
      };
      (inner || this.bar).appendChild(probe);
      const allowed = lines(fill(this.L.barRenewFreeCompact, tokens)) < 3;
      probe.remove();
      this.planDefaultAllowed = allowed;
    }

    /* ---- the button --------------------------------------------------------------------------------------- */
    onCta() {
      const state = this.ctaState();
      if (state === 'adding') return;
      if (state === 'added') return this.openDrawer();
      if (state === 'need-diffuser') {
        this.selectTab('diffusers', { user: true });
        const radio = this.root.querySelector('input[name="se2-kit-diffuser"]:not(:disabled)');
        if (radio) radio.focus();
        return;
      }
      if (state === 'need-fragrance') {
        this.selectTab('fragrances', { user: true });
        const first = this.cards.find((c) => !c.hidden && c.getAttribute('aria-disabled') !== 'true');
        const box = first && first.querySelector('.se2-kit__check');
        if (box) {
          box.focus({ preventScroll: true });
          this.keepInView(first, first.getBoundingClientRect().height);
        }
        return;
      }
      this.add(state === 'diffuser-only');
    }

    openDrawer() {
      const drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
      else window.location.href = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || this.data.routes.cart;
    }

    routes() {
      const r = (window.Theme && window.Theme.routes) || {};
      return {
        add: jsUrl(r.cart_add_url || this.data.routes.cartAdd),
        update: jsUrl(r.cart_update_url || this.data.routes.cartUpdate),
        change: jsUrl(r.cart_change_url || this.data.routes.cartChange),
        cart: jsUrl(r.cart_url || this.data.routes.cart),
        cartPage: r.cart_url || this.data.routes.cart,
      };
    }

    sections() {
      return Array.from(document.querySelectorAll('cart-items-component[data-section-id]')).map((e) => e.dataset.sectionId);
    }

    /* The add sequence (spec C2.10, D.8). */
    async add(diffuserOnly) {
      if (this.busy) return;
      const t = this.totals();
      if (!t.diffuser || !t.diffuser.variant.available) return;
      this.normalize(true);
      const lines = diffuserOnly ? [] : this.state.fragrances.slice();
      if (!diffuserOnly && !lines.length) return;
      const kitId = 'k' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      const free = diffuserOnly ? -1 : this.freeIndex(lines);
      const code = this.offerCode;
      const items = [
        { id: t.diffuser.variant.id, quantity: 1, properties: { _se2_kit: kitId, _se2_kit_role: 'diffuser' } },
      ];
      let gift = null;
      lines.forEach((line, i) => {
        const v = this.lineVariant(line);
        if (!v) return;
        if (i === free && code) {
          gift = { id: v.id, quantity: 1, selling_plan: v.planId, properties: { _se2_kit: kitId, _se2_kit_role: 'gift', _se2_kit_gift: code } };
          items.push(gift);
          return;
        }
        const item = { id: v.id, quantity: 1, properties: { _se2_kit: kitId, _se2_kit_role: 'scent' } };
        if (line.plan && v.planId) item.selling_plan = v.planId;
        items.push(item);
      });
      this.busy = true;
      this.hideNotice();
      this.renderBar();
      const routes = this.routes();
      const sections = this.sections();
      let existing = [];
      // The cart before the kit goes in: its codes (kept, whatever happens) and whether an automatic discount was
      // already there (as opposed to one the kit's own lines trigger).
      let autoBefore = false;
      // A failed read means the codes on the cart are unknown: the kit then writes no code at all (writing the offer
      // code alone would replace the shopper's codes, and its restore would clear them; QA 2026-10-06, S-kit-05).
      let readFailed = false;
      if (gift) {
        try {
          const cart = await getCart(routes.cart);
          existing = (cart.discount_codes || []).map((d) => d.code).filter(Boolean);
          autoBefore = hasAutomatic(cart);
        } catch (e) {
          existing = [];
          readFailed = true;
        }
      }
      this.autoBefore = autoBefore;
      let added;
      try {
        added = await postJson(routes.add, { items, sections, sections_url: window.location.pathname });
      } catch (e) {
        added = { ok: false, body: null };
      }
      if (!added.ok) {
        this.busy = false;
        this.renderBar();
        const description = (added.body && (added.body.description || added.body.message)) || '';
        this.showNotice({ kind: 'add-error', description });
        return;
      }
      this.kitId = kitId;
      this.lastItems = items;
      let outcome;
      if (!gift) outcome = await this.plainOutcome(routes, added.body);
      else if (readFailed) {
        // The kit is in the cart, its bottle not yet free: the "could not be checked" notice, whose Try again reads
        // the cart afresh and applies the code beside the codes it finds.
        outcome = await this.errorOutcome(routes);
        if (outcome.cartJson) outcome.sectionsHtml = added.body && added.body.sections;
      } else outcome = await this.applyOffer({ kitId, code, existing, sections, restore: true, autoBefore });
      await this.finishAdd(items, outcome, kitId, code);
    }

    async plainOutcome(routes, addBody) {
      let cartJson = null;
      try {
        cartJson = await getCart(routes.cart);
      } catch (e) {
        cartJson = null;
      }
      return { state: 'none', cartJson, sectionsHtml: addBody && addBody.sections };
    }

    /**
     * Applies the code and reads the cart back: applied | one_per_order | not_applicable | conflict | error.
     *
     * existing: the codes on the cart before (always restored when the code does not apply, so the shopper never
     * loses a code). only: send the offer code alone (the notice's "Use the free first bottle"). autoBefore: an
     * automatic discount was on the cart before the kit went in. The outcome's memo is true only when the code does
     * not apply and nothing else explains it; the caller then stops calling bottles free for the session.
     */
    async applyOffer({ kitId, code, existing, sections, restore, only, autoBefore }) {
      const routes = this.routes();
      const discount = only ? code : dedupe(existing.concat([code])).join(',');
      let data;
      try {
        const res = await postJson(routes.update, { discount, sections, sections_url: window.location.pathname });
        if (!res.ok) throw new Error('update');
        data = res.body;
      } catch (e) {
        return this.errorOutcome(routes);
      }
      const codes = data.discount_codes || [];
      const hit = codes.find((d) => upper(d.code) === upper(code));
      const kitLines = (data.items || []).filter((i) => i.properties && i.properties._se2_kit === kitId);
      const giftLine = kitLines.find((i) => i.properties._se2_kit_gift) || null;
      const autoNow = hasAutomatic(data);
      if (hit && hit.applicable === true) {
        if (!giftLine) return { state: 'error', cartJson: data, sectionsHtml: data.sections, giftLine: null };
        if (giftLine.final_line_price === 0) return { state: 'applied', cartJson: data, sectionsHtml: data.sections, giftLine };
        // The code gets one bottle of the offer size on a plan. With two such subscribed bottles in the kit (the
        // gift and an extra switched to Subscribe & save, both $39.99), Shopify may discount the extra instead:
        // the order is still right, so this is applied too.
        const freeLine = kitLines.find((i) => i.selling_plan_allocation && Number(i.final_line_price) === 0);
        if (freeLine) return { state: 'applied', cartJson: data, sectionsHtml: data.sections, giftLine: freeLine };
        // The offer is a bottle on Subscribe & save (OD2). A code that discounted only lines with no plan (a one-time
        // bottle) did not give it: that is "not applicable", so the code comes off again and the notice and the
        // total say what the cart charges (QA 2026-10-06, S-kit-04).
        if (!this.codeOffPlan(data, code)) return { state: 'one_per_order', cartJson: data, sectionsHtml: data.sections, giftLine };
      }
      // Not applicable: take the dead code off again and keep every code the shopper already had.
      const keepCodes = existing.slice();
      let restored = data;
      if (restore !== false) {
        try {
          const res = await postJson(routes.update, { discount: keepCodes.join(','), sections, sections_url: window.location.pathname });
          if (!res.ok) throw new Error('restore');
          restored = res.body;
        } catch (e) {
          return this.errorOutcome(routes);
        }
      }
      const restoredGift =
        (restored.items || []).find((i) => i.properties && i.properties._se2_kit === kitId && i.properties._se2_kit_gift) || giftLine;
      // Only a code that actually applies can be what the free bottle does not combine with: a dead code the
      // shopper typed in the drawer stays on the cart (Horizon keeps it) but explains nothing.
      const liveCodes = only
        ? []
        : keepCodes.filter((c) => codes.some((d) => upper(d.code) === upper(c) && d.applicable === true));
      const base = { cartJson: restored, sectionsHtml: restored.sections, giftLine: restoredGift };
      if (liveCodes.length) return Object.assign(base, { state: 'conflict', conflictCode: liveCodes[0] });
      if (autoBefore) return Object.assign(base, { state: 'conflict', conflictCode: null });
      // An automatic discount the kit's own lines triggered: the bottle did not apply to this order, but the code
      // may apply to another kit, so the session keeps the offer.
      const memo = !autoNow;
      if (memo) session.set('se2-kit:offer', JSON.stringify({ state: 'unavailable', code, at: Date.now() }));
      return Object.assign(base, { state: 'not_applicable', memo });
    }

    /** The code discounted at least one line, and none of those lines is on a selling plan. */
    codeOffPlan(cart, code) {
      const want = upper(code);
      const lines = (cart.items || []).filter(
        (i) =>
          (i.line_level_discount_allocations || []).some((x) => {
            const app = x.discount_application || {};
            return Number(x.amount) > 0 && (upper(app.key) === want || upper(app.title) === want);
          }) || (i.discounts || []).some((d) => Number(d.amount) > 0 && upper(d.title) === want)
      );
      return lines.length > 0 && !lines.some((i) => i.selling_plan_allocation);
    }

    async errorOutcome(routes) {
      let cartJson = null;
      try {
        cartJson = await getCart(routes.cart);
      } catch (e) {
        cartJson = null;
      }
      const giftLine = cartJson
        ? (cartJson.items || []).find((i) => i.properties && i.properties._se2_kit === this.kitId && i.properties._se2_kit_gift) || null
        : null;
      return { state: 'error', cartJson, sectionsHtml: undefined, giftLine };
    }

    async dispatchCart(outcome, items, action) {
      if (!outcome.cartJson) return false;
      try {
        const { CartLinesUpdateEvent } = await import('@shopify/events');
        const deferred = CartLinesUpdateEvent.createPromise();
        this.root.dispatchEvent(
          new CartLinesUpdateEvent({
            action,
            context: 'product',
            lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: 1 })),
            promise: deferred.promise,
          })
        );
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(outcome.cartJson),
          detail: {
            sections: outcome.sectionsHtml || undefined,
            items: outcome.cartJson.items,
            itemCount: outcome.cartJson.item_count,
            source: 'se2-kit',
            didError: false,
          },
        });
        return true;
      } catch (e) {
        return false;
      }
    }

    async finishAdd(items, outcome, kitId, code) {
      const panelState = ['not_applicable', 'one_per_order', 'conflict', 'error'].includes(outcome.state);
      const action = panelState ? 'update' : 'add';
      const dispatched = await this.dispatchCart(outcome, items, action);
      this.busy = false;
      this.added = true;
      this.addedOnce = true;
      this.giftLine = outcome.giftLine || null;
      this.lastOutcome = outcome;
      this.kitCart = outcome.cartJson || null;
      // The cart is the truth until the next edit: a bottle it did not make free is not called free anywhere on
      // the page (card, tab, stage, bar, breakdown), whatever the reason (spec C2.10).
      this.giftCharged = panelState;
      if (outcome.memo) this.offerLive = false;
      this.renderAll({});
      this.say(this.L.annKitAdded);
      const applied =
        outcome.state === 'applied' ? true : outcome.state === 'none' || outcome.state === 'error' ? null : false;
      document.dispatchEvent(
        new CustomEvent('se2:kit:added', {
          bubbles: true,
          detail: {
            kitId,
            items: items.map((i) => ({
              id: i.id,
              quantity: i.quantity,
              role: i.properties._se2_kit_role,
              sellingPlan: i.selling_plan || null,
            })),
            offer: { code: code || '', state: outcome.state, applied },
          },
        })
      );
      publish('se2_kit_add', { items: items.length, total: this.totals().due });
      publish('se2_kit_offer_result', { code: code || '', state: outcome.state });
      if (panelState) {
        this.showNotice({ kind: outcome.state, conflictCode: outcome.conflictCode });
        return;
      }
      const autoDrawer = document.querySelector('cart-drawer-component[auto-open]');
      if (this.data.afterAdd === 'checkout') window.location.href = this.data.routes.checkout || this.routes().cartPage;
      else if (this.data.afterAdd === 'cart' || !dispatched || !autoDrawer) window.location.href = this.routes().cartPage;
    }

    /* ---- the notice panel (spec C2.10) -------------------------------------------------------------------- */
    giftInfo() {
      const line = this.giftLine;
      let product = null;
      let variant = null;
      if (line) {
        for (const f of this.fragrances) {
          const v = this.fvariant(f, line.variant_id || line.id);
          if (v) {
            product = f;
            variant = v;
            break;
          }
        }
      }
      if (!product) {
        const gift = (this.lastItems || []).find((i) => i.properties && i.properties._se2_kit_role === 'gift');
        if (gift) {
          for (const f of this.fragrances) {
            const v = this.fvariant(f, gift.id);
            if (v) {
              product = f;
              variant = v;
              break;
            }
          }
        }
      }
      return { product, variant };
    }

    showNotice({ kind, conflictCode, description, failed }) {
      if (!this.noticeEl) return;
      const L = this.L;
      this.notice = { kind, conflictCode, description };
      const { product, variant } = this.giftInfo();
      const tokens = {
        title: product ? product.title : '',
        size: variant ? variant.title : '',
        plan_price: variant ? this.fmt(variant.planPrice) : '',
        every: variant ? variant.planEvery : '',
        code: conflictCode || '',
        price: variant ? this.fmt(variant.price) : '',
      };
      const box = this.noticeEl;
      box.textContent = '';
      // The panel is one sentence, a quiet close button beside it (named for its icon, "Dismiss": closing leaves
      // the cart as the sentence says it is), and one row of text actions. "View cart" is the bar's own button after
      // an add, so the panel does not repeat it. Compact so the fixed bar stays under a quarter of a phone screen
      // (QA 2026-10-06, A-kit-02). With another code on the cart the two real choices, the free bottle or that
      // code, are both visible text actions of equal weight; switching the bottle to one-time stays on its cart line.
      let sentence = '';
      const actions = [];
      if (kind === 'add-error') {
        sentence = (L.error + ' ' + (description || '')).trim();
      } else if (kind === 'not_applicable' || kind === 'one_per_order' || (kind === 'conflict' && !conflictCode)) {
        sentence = fill(kind === 'not_applicable' ? L.noticeNotApplicable : kind === 'one_per_order' ? L.noticeOnePerOrder : L.noticeConflictAuto, tokens);
        actions.push(['switch', fill(L.noticeSwitch, tokens)], ['remove', fill(L.noticeRemove, tokens)]);
      } else if (kind === 'conflict') {
        sentence = fill(L.noticeConflictCode, tokens);
        actions.push(['use', L.noticeUse], ['keep', fill(L.noticeKeepCode, tokens)]);
      } else if (kind === 'error') {
        sentence = fill(L.noticeError, tokens);
        actions.push(['retry', L.noticeRetry]);
      }
      // The panel is a group named by its sentence and takes focus (so its actions are one Tab away); only a
      // failure is an alert, so nothing is spoken twice.
      const head = el('div', 'se2-kit__noticehead');
      const text = el('p', 'se2-kit__noticetext', nb(sentence));
      text.id = 'se2-kit-notice-t-' + this.id;
      if (kind === 'add-error') text.setAttribute('role', 'alert');
      box.setAttribute('aria-labelledby', text.id);
      head.appendChild(text);
      const x = el('button', 'se2-kit__noticex');
      x.type = 'button';
      x.setAttribute('data-se2-kit-act', 'dismiss');
      x.setAttribute('aria-label', L.noticeDismiss || 'Dismiss');
      x.title = L.noticeDismiss || 'Dismiss';
      x.innerHTML =
        '<svg viewBox="0 0 18 18" width="18" height="18" fill="none" focusable="false" aria-hidden="true"><path d="m4.5 4.5 9 9m0-9-9 9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
      head.appendChild(x);
      box.appendChild(head);
      if (actions.length) {
        const row = el('div', 'se2-kit__noticelinks');
        actions.forEach(([act, label]) => {
          const b = el('button', 'se2-kit__textbtn', nb(label));
          b.type = 'button';
          b.setAttribute('data-se2-kit-act', act);
          row.appendChild(b);
        });
        box.appendChild(row);
      }
      if (failed) {
        const err = el('p', 'se2-kit__noticeerr', L.noticeFailed);
        err.setAttribute('role', 'alert');
        box.appendChild(err);
      }
      // The kind drives the CSS: below 990 a post-add notice (its sentence states the plan price and cadence) takes
      // the renew row's place; an add error leaves the renew row beside the add it is still offering.
      box.setAttribute('data-kind', kind);
      box.hidden = false;
      this.syncMini();
      this.fitBar();
      requestAnimationFrame(() => box.focus({ preventScroll: true }));
    }

    hideNotice() {
      if (!this.noticeEl) return;
      this.noticeEl.hidden = true;
      this.noticeEl.removeAttribute('data-kind');
      this.noticeEl.textContent = '';
      this.notice = null;
      this.syncMini();
      this.fitBar();
    }

    async onNoticeAction(act) {
      const routes = this.routes();
      const sections = this.sections();
      const { product, variant } = this.giftInfo();
      const title = product ? product.title : '';
      if (act === 'dismiss' || act === 'keep') {
        this.hideNotice();
        this.cta.focus();
        return;
      }
      const kind = this.notice ? this.notice.kind : '';
      const conflictCode = this.notice ? this.notice.conflictCode : null;
      try {
        if (act === 'switch' || act === 'remove') {
          if (!this.giftLine || !this.giftLine.key) throw new Error('no line');
          const body =
            act === 'switch'
              ? { id: this.giftLine.key, quantity: 1, selling_plan: null, sections, sections_url: window.location.pathname }
              : { id: this.giftLine.key, quantity: 0, sections, sections_url: window.location.pathname };
          const res = await postJson(routes.change, body);
          if (!res.ok) throw new Error('change');
          await this.dispatchCart({ cartJson: res.body, sectionsHtml: res.body.sections }, [{ id: variant ? variant.id : 0 }], 'update');
          // The kit follows the cart: the bottle is one-time (or gone) here too, and the kit stays "added".
          const pid = product ? String(product.id) : '';
          const index = this.state.fragrances.findIndex((l) => String(l.productId) === pid);
          if (index >= 0) {
            if (act === 'switch') this.state.fragrances[index].plan = false;
            else this.state.fragrances.splice(index, 1);
          }
          this.kitCart = res.body;
          this.giftCharged = true;
          this.giftLine =
            act === 'switch'
              ? (res.body.items || []).find((i) => i.properties && i.properties._se2_kit === this.kitId && i.properties._se2_kit_gift) || null
              : null;
          this.hideNotice();
          this.save();
          this.renderAll({});
          this.say(
            act === 'switch'
              ? fill(this.L.annCartOnce, { title, price: variant ? this.fmt(variant.price) : '' })
              : fill(this.L.annCartRemoved, { title })
          );
          this.cta.focus();
          return;
        }
        if (act === 'use' || act === 'retry') {
          // Both start from the codes on the cart now, and restore them if the free bottle still does not apply:
          // "Use the free first bottle" never costs the shopper a code for nothing.
          const cart = await getCart(routes.cart);
          const existing = (cart.discount_codes || []).map((d) => d.code).filter(Boolean);
          const outcome = await this.applyOffer({
            kitId: this.kitId,
            code: this.offerCode,
            existing,
            sections,
            restore: true,
            only: act === 'use',
            autoBefore: this.autoBefore,
          });
          if (outcome.state === 'error' && !outcome.cartJson) throw new Error('error');
          this.giftLine = outcome.giftLine || this.giftLine;
          this.lastOutcome = outcome;
          if (outcome.cartJson) this.kitCart = outcome.cartJson;
          const panel = ['not_applicable', 'one_per_order', 'conflict', 'error'].includes(outcome.state);
          await this.dispatchCart(outcome, this.lastItems || [], 'update');
          publish('se2_kit_offer_result', { code: this.offerCode, state: outcome.state });
          this.giftCharged = panel;
          if (outcome.memo) this.offerLive = false;
          this.renderAll({});
          if (outcome.state === 'applied') {
            this.hideNotice();
            this.say(this.L.annFirstFree);
            this.openDrawer();
            return;
          }
          if (panel) this.showNotice({ kind: outcome.state, conflictCode: outcome.conflictCode });
          return;
        }
      } catch (e) {
        this.showNotice({ kind, conflictCode, failed: true });
      }
    }

    /* ---- the sheet (Details, View features, the breakdown) ----------------------------------------------- */
    openSheet(kind, opener, arg) {
      if (!this.sheet || !this.sheetBody) return;
      this.sheetOpener = opener || null;
      this.sheetKind = kind;
      this.sheetArg = arg;
      this.drawSheet();
      if (!this.sheet.open) {
        try {
          this.sheet.showModal();
        } catch (e) {
          this.sheet.setAttribute('open', '');
        }
      }
      if (kind === 'breakdown' && this.total) this.total.setAttribute('aria-expanded', 'true');
      const heading = this.sheet.querySelector('.se2-kit__sheettitle');
      if (heading) heading.focus({ preventScroll: true });
    }

    closeSheet() {
      if (!this.sheet || !this.sheet.open) return;
      if (reduced() || !this.sheet.animate) {
        this.sheet.close();
        return;
      }
      const panel = this.sheet.querySelector('.se2-kit__sheetin');
      const anim = panel.animate(
        narrow() ? [{ transform: 'none' }, { transform: 'translateY(100%)' }] : [{ opacity: 1 }, { opacity: 0 }],
        { duration: 200, easing: EASE }
      );
      anim.onfinish = () => this.sheet.close();
    }

    onSheetClosed() {
      if (this.total) this.total.setAttribute('aria-expanded', 'false');
      const opener = this.sheetOpener;
      this.sheetOpener = null;
      this.sheetKind = null;
      if (opener && opener.isConnected && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
    }

    sheetRow(label, figure, opts = {}) {
      const row = el('div', 'se2-kit__srow' + (opts.cls ? ' ' + opts.cls : ''));
      const left = el('div', 'se2-kit__sl');
      left.appendChild(el('span', 'se2-kit__slabel', label));
      if (opts.sub) left.appendChild(el('span', 'se2-kit__ssub', opts.sub));
      const right = el('div', 'se2-kit__sr');
      right.appendChild(el('span', 'se2-kit__sfig', figure));
      if (opts.figSub) right.appendChild(el('span', 'se2-kit__ssub', opts.figSub));
      row.appendChild(left);
      row.appendChild(right);
      return row;
    }

    drawSheet() {
      const body = this.sheetBody;
      const L = this.L;
      body.textContent = '';
      const titleId = 'se2-kit-sheet-t-' + this.id;
      const heading = (text) => {
        const h = el('h2', 'se2-kit__sheettitle', text);
        h.id = titleId;
        h.tabIndex = -1;
        return h;
      };
      if (this.sheetKind === 'breakdown') {
        const t = this.totals();
        body.appendChild(heading(L.breakdownTitle));
        const list = el('div', 'se2-kit__slist');
        if (t.diffuser) {
          const d = t.diffuser;
          const name = d.product.title + ((d.product.variants || []).length > 1 ? ', ' + d.variant.color : '');
          list.appendChild(this.sheetRow(name, this.fmt(d.variant.price)));
        }
        this.state.fragrances.forEach((line, i) => {
          const p = this.fragrance(line.productId);
          const v = this.lineVariant(line);
          if (!p || !v) return;
          const name = p.title + ', ' + v.title;
          if (i === t.free) {
            const row = this.sheetRow(name, L.shippingFree, {
              sub: fill(L.subscribeEvery, { every: v.planEvery }),
              figSub: fill(L.value, { value: this.fmt(v.price) }),
            });
            row.appendChild(el('p', 'se2-kit__sthen', fill(L.thenPrice, { price: this.fmt(v.planPrice), every: v.planEvery })));
            list.appendChild(row);
          } else if (line.plan && v.planId) {
            list.appendChild(
              this.sheetRow(name, this.fmt(v.planPrice), {
                sub: fill(L.subscribeSaveEvery, { percent: v.planPercent, every: v.planEvery }),
              })
            );
          } else {
            list.appendChild(this.sheetRow(name, this.fmt(v.price), { sub: L.once }));
          }
        });
        // Shipping, by the cart's own rule (snippets/se2-cart-shipping-progress.liquid, the one place it is written):
        // a kit with every line on a plan ships free at any value; otherwise its one-time part (the diffuser always
        // is) counts toward the threshold, because checkout prices plan lines and one-time lines as two delivery
        // groups. So a subscribed kit under the threshold reads "At checkout" here and "$X away" in the cart, and
        // its note says which part ships free (QA 2026-10-06, A-kit-01). If QA B3c shows a mixed cart ships free as
        // one order, this and the snippet's fs_all_on_plan change together.
        // "Free" always carries its condition (VOICE.md section 4): the subscription rule on an all-plan kit, the
        // one-time rule (or the mixed note) once the one-time part reaches the threshold, e.g. every SCENTPRO Plus kit.
        const threshold = Number(this.data.freeShippingCents) || 0;
        const allPlan = t.anyPlan && t.oneTime === 0;
        const thresholdText = this.data.freeShippingText || this.fmt(threshold);
        const note = nb(fill(t.anyPlan && L.shippingNoteMixed ? L.shippingNoteMixed : L.shippingNote || '', { threshold: thresholdText }));
        if (allPlan || (threshold > 0 && t.oneTime >= threshold)) {
          const freeSub = allPlan ? L.shippingSubscription : note;
          list.appendChild(this.sheetRow(L.shipping, L.shippingFree, freeSub ? { sub: freeSub } : {}));
        } else if (threshold > 0) {
          list.appendChild(this.sheetRow(L.shipping, L.shippingAtCheckout, { sub: note }));
        }
        // In the cart as configured: what the cart charges, with any line discount it applied shown as its own row.
        if (t.cartDue != null && t.cartDue < t.due) {
          list.appendChild(this.sheetRow(L.cartDiscounts, '\u2212' + this.fmt(t.due - t.cartDue)));
        }
        list.appendChild(this.sheetRow(L.dueToday, this.fmt(t.cartDue != null ? t.cartDue : t.due), { cls: 'is-total' }));
        if (t.anyPlan && t.cadence !== null) list.appendChild(this.sheetRow(fill(L.thenEvery, { every: t.cadence }), this.fmt(t.recurring)));
        body.appendChild(list);
        if (t.anyPlan) {
          const p = el('p', 'se2-kit__spause', L.pause);
          if (this.data.routes && this.data.routes.subscriptionPolicy) {
            p.appendChild(document.createTextNode(' '));
            const a = el('a', 'se2-kit__terms', L.terms);
            a.href = this.data.routes.subscriptionPolicy;
            p.appendChild(a);
          }
          body.appendChild(p);
        }
        // The promises no longer print on the page (critique 2026-10-15); they wait in a <template> for this sheet.
        const source = this.root.querySelector('template[data-se2-kit-promises]');
        const promises = source && source.content ? source.content.querySelector('.se2-kit__promises') : null;
        if (promises) {
          const copy = promises.cloneNode(true);
          copy.classList.add('se2-kit__promises--sheet');
          copy.querySelectorAll('[data-shopify-editor-block]').forEach((n) => n.removeAttribute('data-shopify-editor-block'));
          body.appendChild(copy);
        }
        return;
      }
      if (this.sheetKind === 'diffuser') {
        const t = this.totals();
        const d = t.diffuser;
        if (!d) return;
        const p = d.product;
        const media = el('div', 'se2-kit__smedia');
        if (d.variant.image) {
          const img = el('img');
          img.src = d.variant.image;
          img.alt = '';
          img.width = d.variant.w || 1;
          img.height = d.variant.h || 1;
          media.appendChild(img);
        }
        body.appendChild(media);
        body.appendChild(heading(p.title));
        if (p.bandLabel) body.appendChild(el('p', 'se2-kit__seyebrow', p.bandLabel + (p.coverageLine ? ' · ' + p.coverageLine : '')));
        const dl = el('dl', 'se2-kit__sfacts');
        const fact = (k, v) => {
          if (!v) return;
          const wrap = el('div', 'se2-kit__sfact');
          wrap.appendChild(el('dt', '', k));
          wrap.appendChild(el('dd', '', v));
          dl.appendChild(wrap);
        };
        fact(L.what, p.what);
        fact(L.bestFor, p.bestFor ? p.bestFor + '.' : '');
        fact(L.holds, p.holds);
        fact(L.control, p.controlLine || p.control);
        if ((p.variants || []).length > 1) fact(L.colors, p.variants.map((v) => v.color + (v.available ? '' : ' (' + L.soldOut + ')')).join(', '));
        body.appendChild(dl);
        body.appendChild(el('p', 'se2-kit__scold', L.coldAir));
        const a = el('a', 'se2-kit__textlink', L.fullDetails);
        a.href = p.url;
        body.appendChild(a);
        return;
      }
      if (this.sheetKind === 'fragrance') {
        const f = this.fragrance(this.sheetArg);
        if (!f) return;
        const chosen = this.state.fragrances.some((l) => String(l.productId) === String(f.id));
        const media = el('div', 'se2-kit__smedia se2-kit__smedia--bottle');
        if (f.image) {
          const img = el('img');
          img.src = f.image;
          img.alt = '';
          img.width = 480;
          img.height = 480;
          media.appendChild(img);
        }
        body.appendChild(media);
        body.appendChild(heading(f.title));
        if (f.family) {
          const tag = el('span', 'se2-kit__tag', f.family);
          tag.setAttribute('data-se2-scent', f.familySlug || '');
          const p = el('p', 'se2-kit__stags');
          p.appendChild(tag);
          body.appendChild(p);
        }
        const dl = el('dl', 'se2-kit__sfacts');
        const fact = (k, v) => {
          if (!v) return;
          const wrap = el('div', 'se2-kit__sfact');
          wrap.appendChild(el('dt', '', k));
          wrap.appendChild(el('dd', '', v));
          dl.appendChild(wrap);
        };
        const notes = f.notes || {};
        fact(L.top, (notes.top || []).filter(Boolean).join(' · '));
        fact(L.heart, (notes.heart || []).filter(Boolean).join(' · '));
        fact(L.base, (notes.base || []).filter(Boolean).join(' · '));
        fact(L.feelsLike, (f.feels || []).filter(Boolean).join(' · '));
        fact(L.strength, f.strength);
        fact(
          L.sizes,
          (f.variants || [])
            .map((v) => v.title + ' ' + this.fmt(v.price) + (v.available ? '' : ' (' + L.soldOut + ')'))
            .join(' · ')
        );
        body.appendChild(dl);
        const planSizes = (f.variants || []).filter((v) => v.planId).map((v) => v.title.replace(/\s*mL$/i, ''));
        const pct = (f.variants || []).find((v) => v.planId);
        if (planSizes.length && pct) {
          const list =
            planSizes.length > 1 ? planSizes.slice(0, -1).join(', ') + ' and ' + planSizes[planSizes.length - 1] + ' mL' : planSizes[0] + ' mL';
          body.appendChild(el('p', 'se2-kit__scold', fill(L.planSentence, { percent: pct.planPercent, sizes: list })));
        }
        const actions = el('div', 'se2-kit__sactions');
        if (f.available) {
          const b = el(
            'button',
            chosen ? 'se2-btn se2-btn--secondary se2-btn--block' : 'se2-btn se2-btn--primary se2-btn--block',
            fill(chosen ? L.remove : L.choose, { title: f.title })
          );
          b.type = 'button';
          b.setAttribute('data-se2-kit-sheet-act', chosen ? 'remove' : 'choose');
          b.setAttribute('data-product-id', String(f.id));
          b.disabled = !chosen && this.state.fragrances.length >= this.max;
          actions.appendChild(b);
        }
        const a = el('a', 'se2-kit__textlink', L.fullDetails);
        a.href = f.url;
        actions.appendChild(a);
        body.appendChild(actions);
      }
    }

    onSheetAction(button) {
      const act = button.getAttribute('data-se2-kit-sheet-act');
      const id = button.getAttribute('data-product-id');
      const card = this.cardFor(id);
      this.sheetOpener = card ? card.querySelector('.se2-kit__check') : this.sheetOpener;
      if (act === 'choose') {
        this.sheet.close();
        if (this.state.tab !== 'fragrances') this.selectTab('fragrances', {});
        this.toggleFragrance(id, true, card);
      } else if (act === 'remove') {
        this.sheet.close();
        this.toggleFragrance(id, false, card);
      }
    }

    /* ---- the sticky tab bar and the header (spec C2.5) ---------------------------------------------------- */
    setupObservers() {
      const mq = window.matchMedia('(max-width: 989px)');
      const apply = () => {
        this.teardownObservers();
        this.syncMini();
        if (!('IntersectionObserver' in window) || !this.sentinels.header || !this.sentinels.stuck) return;
        if (!mq.matches) {
          // 990 and up: the header stays; the tab bar sticks at 96 and only needs its stuck class.
          document.documentElement.removeAttribute('data-se2-kit-header-hidden');
          this.setStuck(false);
          this.stuckIO = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => this.setStuck(entry.intersectionRatio < 1 && entry.boundingClientRect.top < 97));
            },
            { rootMargin: '-96px 0px 0px 0px', threshold: [0, 1] }
          );
          this.stuckIO.observe(this.sentinels.stuck);
          return;
        }
        const headerBottom = this.headerBottom();
        this.headerIO = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const above = !entry.isIntersecting && entry.boundingClientRect.top < headerBottom;
              document.documentElement.toggleAttribute('data-se2-kit-header-hidden', above);
            });
          },
          { rootMargin: '-' + headerBottom + 'px 0px 0px 0px', threshold: 0 }
        );
        this.headerIO.observe(this.sentinels.header);
        this.stuckIO = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // Stuck once the 1px sentinel right above the bar has started to leave the top of the viewport.
              this.setStuck(entry.intersectionRatio < 1 && entry.boundingClientRect.top < 1);
            });
          },
          { rootMargin: '0px', threshold: [0, 1] }
        );
        this.stuckIO.observe(this.sentinels.stuck);
      };
      apply();
      const onChange = () => apply();
      if (mq.addEventListener) {
        mq.addEventListener('change', onChange);
        this.cleanups.push(() => mq.removeEventListener('change', onChange));
      }
      this.cleanups.push(() => this.teardownObservers());
    }

    teardownObservers() {
      if (this.headerIO) this.headerIO.disconnect();
      if (this.stuckIO) this.stuckIO.disconnect();
      this.headerIO = null;
      this.stuckIO = null;
    }

    headerBottom() {
      const hc = document.getElementById('header-component');
      const section = hc && hc.closest('.header-section');
      if (hc && section) {
        const bottom = hc.getBoundingClientRect().bottom - section.getBoundingClientRect().top;
        if (bottom > 0 && bottom < 200) return Math.round(bottom);
      }
      return 64;
    }

    setStuck(on) {
      if (this.stuck === on) return;
      this.stuck = on;
      if (this.tabbar) this.tabbar.classList.toggle('is-stuck', on);
      this.syncMini();
      requestAnimationFrame(() => this.placeTabIndicator());
    }

    // The mini kit is reachable only while it shows: the tab bar is stuck and, below 990, no notice has made the tab
    // bar let go (assets/se2-kit.css scrolls it with the page then, so the mini would sit off-screen and a focus on it
    // would scroll the page and drop focus; QA 2026-10-06, A-kit-02). Otherwise it is out of the tab order and the
    // accessibility tree. The stuck class stays as the observer set it, so the in-flow tab bar keeps its height.
    syncMini() {
      if (!this.mini) return;
      const parked = narrow() && Boolean(this.noticeEl) && !this.noticeEl.hidden;
      const live = Boolean(this.stuck) && !parked;
      this.mini.tabIndex = live ? 0 : -1;
      this.mini.inert = !live;
    }

    scrollToTabs(instant) {
      if (!this.sentinels.stuck) return;
      const top = Math.ceil(this.sentinels.stuck.getBoundingClientRect().top + window.scrollY) + 1;
      window.scrollTo({ top, behavior: instant ? 'instant' : reduced() ? 'instant' : 'smooth' });
    }

    scrollPanelIntoView() {
      const panel = this.panels[this.state.tab];
      if (!panel) return;
      panel.scrollIntoView({ block: 'start', behavior: reduced() ? 'instant' : 'smooth' });
    }

    showStage() {
      if (!this.stage) return;
      this.stage.scrollIntoView({ block: 'start', behavior: reduced() ? 'instant' : 'smooth' });
      this.stage.focus({ preventScroll: true });
    }

    /* ---- announcements ------------------------------------------------------------------------------------ */
    say(message, force) {
      if (!this.announcer || !message) return;
      if (!force && message === this.announcer.textContent) {
        this.announcer.textContent = '';
        requestAnimationFrame(() => {
          this.announcer.textContent = message;
        });
        return;
      }
      this.announcer.textContent = message;
    }

    destroy() {
      this.cleanups.splice(0).forEach((fn) => {
        try {
          fn();
        } catch (e) {
          /* already gone */
        }
      });
      document.documentElement.removeAttribute('data-se2-kit-header-hidden');
      if (this.sheet && this.sheet.open) this.sheet.close();
    }
  }

  /* ---- boot, and the theme editor ------------------------------------------------------------------------- */
  const kits = new Map();
  const boot = (scope) => {
    (scope || document).querySelectorAll('[data-se2-kit]').forEach((root) => {
      if (kits.has(root)) return;
      const kit = new Kit(root);
      kits.set(root, kit);
      try {
        kit.init();
      } catch (error) {
        console.error('[se2-kit]', error);
      }
    });
  };
  const teardown = (scope) => {
    kits.forEach((kit, root) => {
      if (!scope || scope.contains(root)) {
        kit.destroy();
        kits.delete(root);
      }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => teardown(event.target));
})();
